import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';

const TABS = ['projects', 'planning', 'kanban', 'resources', 'risks', 'communication', 'vks', 'analytics', 'crm', 'catalog'];
const ProjectManagement = ({ currentUser, initialProjectId, initialTab, initialConferenceId }) => {
  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const appliedInitialRef = React.useRef(false);
  const [tasks, setTasks] = useState([]);
  const [ganttData, setGanttData] = useState(null);
  const [members, setMembers] = useState([]);
  const [risks, setRisks] = useState([]);
  const [comments, setComments] = useState([]);
  const [files, setFiles] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [criticalPath, setCriticalPath] = useState(null);
  const [projectResourceSummary, setProjectResourceSummary] = useState({ equipment: [], materials: [] });
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [projectForm, setProjectForm] = useState({ name: '', description: '', status: 'active', priority: 'medium', organization_id: null, responsible_id: null, client_id: null, start_date: '', end_date: '', budget_planned: '' });
  const [editingProject, setEditingProject] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskForm, setTaskForm] = useState({ name: '', description: '', status: 'todo', priority: 'medium', progress: 0, estimated_hours: 0, actual_hours: 0, budget_planned: 0, actual_cost: 0, responsible_id: null, parent_id: null, start_date: '', end_date: '' });
  const [timeEntries, setTimeEntries] = useState([]);
  const [taskDependencies, setTaskDependencies] = useState([]);
  const [resourceAllocations, setResourceAllocations] = useState([]);
  const [timerRunning, setTimerRunning] = useState(null);
  const [timerStartedAt, setTimerStartedAt] = useState(null);
  const [timeEntryDate, setTimeEntryDate] = useState('');
  const [timeEntryHours, setTimeEntryHours] = useState(0.5);
  const [timeEntryComment, setTimeEntryComment] = useState('');
  const [catalogEquipment, setCatalogEquipment] = useState([]);
  const [catalogMaterials, setCatalogMaterials] = useState([]);
  const [dealStages, setDealStages] = useState([]);
  const [deals, setDeals] = useState([]);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [equipmentForm, setEquipmentForm] = useState({ name: '', description: '', unit: 'шт' });
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [materialForm, setMaterialForm] = useState({ name: '', description: '', unit: 'шт' });
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [showDealModal, setShowDealModal] = useState(false);
  const [dealForm, setDealForm] = useState({ name: '', client_id: null, stage_id: null, project_id: null, amount: 0, responsible_id: null });
  const [editingDeal, setEditingDeal] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  const [deleteRiskConfirm, setDeleteRiskConfirm] = useState(null);
  const [riskForm, setRiskForm] = useState({ title: '', description: '', probability: 50, impact: 50, mitigation: '', status: 'open' });
  const [commentText, setCommentText] = useState('');
  const [commentTaskId, setCommentTaskId] = useState(null);
  const [deleteProjectConfirm, setDeleteProjectConfirm] = useState(null);
  const [deleteTaskConfirm, setDeleteTaskConfirm] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientForm, setClientForm] = useState({ name: '', contact_person: '', email: '', phone: '', address: '', notes: '' });
  const [editingClient, setEditingClient] = useState(null);
  const fileInputRef = React.useRef(null);
  const [projectFileUploading, setProjectFileUploading] = useState(false);
  const [projectFileUploadProgress, setProjectFileUploadProgress] = useState(0);
  const [projectFileUploadName, setProjectFileUploadName] = useState('');
  const [projectFileUploadSize, setProjectFileUploadSize] = useState(0);
  const [conferences, setConferences] = useState([]);
  const [showConferenceModal, setShowConferenceModal] = useState(false);
  const [conferenceForm, setConferenceForm] = useState({ title: '', scheduled_at: '', duration_minutes: 60, participant_ids: [] });
  const [editingConference, setEditingConference] = useState(null);
  const [joinUrl, setJoinUrl] = useState(null);
  const [vksFullscreen, setVksFullscreen] = useState(false);
  const [activeConferenceForJoin, setActiveConferenceForJoin] = useState(null);
  const [conferenceRecordings, setConferenceRecordings] = useState({});
  const vksContainerRef = React.useRef(null);
  const [deleteConferenceConfirm, setDeleteConferenceConfirm] = useState(null);
  const [playingVksRecording, setPlayingVksRecording] = useState(null);
  const vksPlayerRef = React.useRef(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('all');
  const [analyticsDateFrom, setAnalyticsDateFrom] = useState('');
  const [analyticsDateTo, setAnalyticsDateTo] = useState('');
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [portfolioProjectIds, setPortfolioProjectIds] = useState([]);
  const [portfolioData, setPortfolioData] = useState(null);

  const token = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${token()}` });

  const getAnalyticsParams = (proj) => {
    const params = {};
    if (analyticsPeriod === 'month') {
      const end = new Date();
      const start = new Date(); start.setDate(start.getDate() - 30);
      params.date_from = start.toISOString().slice(0, 10);
      params.date_to = end.toISOString().slice(0, 10);
    } else if (analyticsPeriod === 'quarter') {
      const end = new Date();
      const start = new Date(); start.setDate(start.getDate() - 90);
      params.date_from = start.toISOString().slice(0, 10);
      params.date_to = end.toISOString().slice(0, 10);
    } else if (analyticsPeriod === 'project' && proj && (proj.start_date || proj.end_date)) {
      if (proj.start_date) params.date_from = proj.start_date.slice(0, 10);
      if (proj.end_date) params.date_to = proj.end_date.slice(0, 10);
    } else if (analyticsDateFrom) params.date_from = analyticsDateFrom.slice(0, 10);
    if (analyticsDateTo) params.date_to = analyticsDateTo.slice(0, 10);
    return params;
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('ru-RU') : '—';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString('ru-RU') : '—';

  const fetchProjects = async () => {
    try {
      const res = await axios.get('/api/projects/', { headers: headers() });
      const list = res.data.projects || [];
      setProjects(list);
      return list;
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка загрузки');
      return [];
    } finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/api/users/', { headers: headers() });
      setUsers(res.data.users || []);
    } catch { setUsers([]); }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get('/api/crm/clients', { headers: headers() });
      setClients(res.data.clients || []);
    } catch { setClients([]); }
  };

  const fetchProjectData = async (id) => {
    if (!id) return;
    try {
      const proj = projects.find(p => p.id === id) || selectedProject;
      const analyticsParams = getAnalyticsParams(proj);
      const [tRes, gRes, mRes, rRes, cRes, fRes, aRes, cpRes, confRes, rsRes] = await Promise.all([
        axios.get(`/api/projects/${id}/tasks/tree`, { headers: headers() }),
        axios.get(`/api/projects/${id}/gantt`, { headers: headers() }),
        axios.get(`/api/projects/${id}/members`, { headers: headers() }),
        axios.get(`/api/projects/${id}/risks`, { headers: headers() }),
        axios.get(`/api/projects/${id}/comments`, { headers: headers() }),
        axios.get(`/api/projects/${id}/files`, { headers: headers() }),
        axios.get(`/api/projects/${id}/analytics`, { params: analyticsParams, headers: headers() }),
        axios.get(`/api/projects/${id}/critical-path`, { headers: headers() }).catch(() => ({ data: null })),
        axios.get(`/api/projects/${id}/conferences`, { headers: headers() }).catch(() => ({ data: { conferences: [] } })),
        axios.get(`/api/projects/${id}/resource-summary`, { headers: headers() }).catch(() => ({ data: { equipment: [], materials: [] } }))
      ]);
      setTasks(tRes.data.tasks || []);
      setGanttData(gRes.data);
      setCriticalPath(cpRes.data);
      setMembers(mRes.data.members || []);
      setRisks(rRes.data.risks || []);
      setComments(cRes.data.comments || []);
      setFiles(fRes.data.files || []);
      setAnalytics(aRes.data);
      setConferences(confRes.data.conferences || []);
      setProjectResourceSummary(rsRes.data || { equipment: [], materials: [] });
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка загрузки данных');
    }
  };

  const fetchAnalyticsOnly = async () => {
    if (!selectedProject) return;
    setAnalyticsLoading(true);
    try {
      const params = getAnalyticsParams(selectedProject);
      const res = await axios.get(`/api/projects/${selectedProject.id}/analytics`, { params, headers: headers() });
      setAnalytics(res.data);
      setSuccess('Аналитика обновлена');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка обновления аналитики');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchPortfolioAnalytics = async () => {
    if (!portfolioProjectIds.length) { setPortfolioData(null); return; }
    try {
      const res = await axios.get(`/api/analytics/portfolio?project_ids=${portfolioProjectIds.join(',')}`, { headers: headers() });
      setPortfolioData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка загрузки портфеля');
      setPortfolioData(null);
    }
  };

  const fetchConferenceRecordings = async (confId) => {
    if (!selectedProject) return;
    try {
      const res = await axios.get(`/api/projects/${selectedProject.id}/conferences/${confId}/recordings`, { headers: headers() });
      setConferenceRecordings(prev => ({ ...prev, [confId]: res.data.recordings || [] }));
    } catch { setConferenceRecordings(prev => ({ ...prev, [confId]: [] })); }
  };

  const openJoinVks = async (conf) => {
    try {
      const res = await axios.get(`/api/projects/${selectedProject.id}/conferences/${conf.id}/join-url`, { headers: headers() });
      setJoinUrl(res.data.join_url);
      setActiveConferenceForJoin(conf);
      fetchConferenceRecordings(conf.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка получения ссылки');
    }
  };

  const createConference = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;
    try {
      const payload = { title: conferenceForm.title, scheduled_at: conferenceForm.scheduled_at || new Date().toISOString().slice(0, 16), duration_minutes: conferenceForm.duration_minutes || 60, participant_ids: conferenceForm.participant_ids || [] };
      await axios.post(`/api/projects/${selectedProject.id}/conferences`, payload, { headers: headers() });
      setSuccess('ВКС создана. Участникам отправлены уведомления.');
      setShowConferenceModal(false);
      setConferenceForm({ title: '', scheduled_at: '', duration_minutes: 60, participant_ids: [] });
      setEditingConference(null);
      fetchProjectData(selectedProject.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка создания ВКС');
    }
  };

  const updateConference = async (e) => {
    e.preventDefault();
    if (!selectedProject || !editingConference) return;
    try {
      await axios.put(`/api/projects/${selectedProject.id}/conferences/${editingConference.id}`, { title: conferenceForm.title, scheduled_at: conferenceForm.scheduled_at, duration_minutes: conferenceForm.duration_minutes, participant_ids: conferenceForm.participant_ids || [] }, { headers: headers() });
      setSuccess('ВКС обновлена');
      setShowConferenceModal(false);
      setEditingConference(null);
      setConferenceForm({ title: '', scheduled_at: '', duration_minutes: 60, participant_ids: [] });
      fetchProjectData(selectedProject.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка обновления');
    }
  };

  const deleteConference = async () => {
    if (!selectedProject || !deleteConferenceConfirm) return;
    try {
      await axios.delete(`/api/projects/${selectedProject.id}/conferences/${deleteConferenceConfirm.id}`, { headers: headers() });
      setSuccess('ВКС удалена');
      setDeleteConferenceConfirm(null);
      if (activeConferenceForJoin?.id === deleteConferenceConfirm.id) { setActiveConferenceForJoin(null); setJoinUrl(null); }
      fetchProjectData(selectedProject.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const fetchOrganizations = async () => {
    try {
      const res = await axios.get('/api/organizations/?limit=500', { headers: headers() });
      setOrganizations(res.data.organizations || []);
    } catch { setOrganizations([]); }
  };
  const fetchCatalogAndDeals = async () => {
    try {
      const [eqRes, matRes, stagesRes, dealsRes] = await Promise.all([
        axios.get('/api/catalog/equipment', { headers: headers() }).catch(() => ({ data: { equipment: [] } })),
        axios.get('/api/catalog/materials', { headers: headers() }).catch(() => ({ data: { materials: [] } })),
        axios.get('/api/crm/deal-stages', { headers: headers() }).catch(() => ({ data: { stages: [] } })),
        axios.get('/api/crm/deals', { headers: headers() }).catch(() => ({ data: { deals: [] } }))
      ]);
      setCatalogEquipment(eqRes.data.equipment || []);
      setCatalogMaterials(matRes.data.materials || []);
      setDealStages(stagesRes.data.stages || []);
      setDeals(dealsRes.data.deals || []);
    } catch { }
  };
  useEffect(() => { fetchProjects(); fetchUsers(); fetchClients(); fetchOrganizations(); }, []);
  useEffect(() => { fetchCatalogAndDeals(); }, []);
  useEffect(() => { if (selectedProject) fetchProjectData(selectedProject.id); }, [selectedProject?.id]);
  useEffect(() => {
    if (activeTab === 'vks' && selectedProject && conferences.length > 0) {
      conferences.forEach(c => {
        axios.get(`/api/projects/${selectedProject.id}/conferences/${c.id}/recordings`, { headers: headers() })
          .then(res => setConferenceRecordings(prev => ({ ...prev, [c.id]: res.data.recordings || [] })))
          .catch(() => setConferenceRecordings(prev => ({ ...prev, [c.id]: [] })));
      });
    }
  }, [activeTab, selectedProject?.id, conferences.length]);
  useEffect(() => {
    if (appliedInitialRef.current || !projects.length) return;
    const pid = initialProjectId ? Number(initialProjectId) : null;
    const tab = initialTab && TABS.includes(initialTab) ? initialTab : null;
    const cid = initialConferenceId ? Number(initialConferenceId) : null;
    if (pid) {
      const p = projects.find(x => x.id === pid);
      if (p) setSelectedProject(p);
    }
    if (tab) setActiveTab(tab);
    if (pid || tab) appliedInitialRef.current = true;
  }, [projects, initialProjectId, initialTab]);
  const openedConferenceIdRef = React.useRef(null);
  useEffect(() => {
    if (!initialConferenceId || !selectedProject || conferences.length === 0) return;
    const cid = Number(initialConferenceId);
    if (openedConferenceIdRef.current === cid) return;
    const conf = conferences.find(c => c.id === cid);
    if (conf) { openedConferenceIdRef.current = cid; openJoinVks(conf); }
  }, [selectedProject?.id, conferences, initialConferenceId]);
  useEffect(() => { const t = setTimeout(() => { setSuccess(''); setError(''); }, 5000); return () => clearTimeout(t); }, [success, error]);

  const createProject = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...projectForm, start_date: projectForm.start_date || null, end_date: projectForm.end_date || null, budget_planned: projectForm.budget_planned ? parseInt(projectForm.budget_planned, 10) || 0 : 0, organization_id: projectForm.organization_id || null, responsible_id: projectForm.responsible_id || null };
      await axios.post('/api/projects/', payload, { headers: headers() });
      setSuccess('Проект создан');
      setShowProjectModal(false);
      setProjectForm({ name: '', description: '', status: 'active', priority: 'medium', organization_id: null, responsible_id: null, client_id: null, start_date: '', end_date: '', budget_planned: '', budget_spent: '' });
      setEditingProject(null);
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка создания');
    }
  };

  const updateProject = async (e) => {
    e.preventDefault();
    if (!editingProject) return;
    try {
      await axios.put(`/api/projects/${editingProject.id}`, { ...projectForm, start_date: projectForm.start_date || null, end_date: projectForm.end_date || null, budget_planned: projectForm.budget_planned ? parseInt(projectForm.budget_planned, 10) || 0 : 0, organization_id: projectForm.organization_id || null, responsible_id: projectForm.responsible_id || null }, { headers: headers() });
      setSuccess('Проект обновлён');
      setShowProjectModal(false);
      setEditingProject(null);
      setProjectForm({ name: '', description: '', status: 'active', priority: 'medium', organization_id: null, responsible_id: null, client_id: null, start_date: '', end_date: '', budget_planned: '' });
      fetchProjects();
      if (selectedProject?.id === editingProject.id) setSelectedProject(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка обновления');
    }
  };

  const deleteProject = async () => {
    if (!deleteProjectConfirm) return;
    try {
      await axios.delete(`/api/projects/${deleteProjectConfirm.id}`, { headers: headers() });
      setSuccess('Проект удалён');
      setDeleteProjectConfirm(null);
      if (selectedProject?.id === deleteProjectConfirm.id) setSelectedProject(null);
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;
    try {
      const payload = {
        name: taskForm.name,
        description: taskForm.description || null,
        status: taskForm.status || 'todo',
        priority: taskForm.priority || 'medium',
        progress: parseInt(taskForm.progress, 10) || 0,
        start_date: taskForm.start_date || null,
        end_date: taskForm.end_date || null,
        budget_planned: parseInt(taskForm.budget_planned, 10) || 0,
        actual_cost: taskForm.actual_cost != null ? parseInt(taskForm.actual_cost, 10) : 0,
        estimated_hours: Math.round(parseFloat(taskForm.estimated_hours) || 0),
        parent_id: taskForm.parent_id || null,
        responsible_id: taskForm.responsible_id || null
      };
      await axios.post(`/api/projects/${selectedProject.id}/tasks`, payload, { headers: headers() });
      setSuccess('Задача создана');
      setShowTaskModal(false);
      setTaskForm({ name: '', description: '', status: 'todo', priority: 'medium', progress: 0, estimated_hours: 0, actual_hours: 0, budget_planned: 0, actual_cost: 0, responsible_id: null, parent_id: null, start_date: '', end_date: '' });
      setEditingTask(null);
      await fetchProjectData(selectedProject.id);
      const list = await fetchProjects();
      if (selectedProject && list.length) setSelectedProject(list.find(p => p.id === selectedProject.id) || selectedProject);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join('; ') : 'Ошибка создания'));
    }
  };

  const updateTask = async (e) => {
    e.preventDefault();
    if (!selectedProject || !editingTask) return;
    try {
      const payload = {
        name: taskForm.name,
        description: taskForm.description || null,
        status: taskForm.status,
        priority: taskForm.priority,
        progress: parseInt(taskForm.progress, 10) || 0,
        start_date: taskForm.start_date || null,
        end_date: taskForm.end_date || null,
        budget_planned: parseInt(taskForm.budget_planned, 10) || 0,
        actual_cost: taskForm.actual_cost != null ? parseInt(taskForm.actual_cost, 10) : 0,
        estimated_hours: Math.round(parseFloat(taskForm.estimated_hours) || 0),
        actual_hours: Math.round(parseFloat(taskForm.actual_hours) || 0),
        parent_id: taskForm.parent_id || null,
        responsible_id: taskForm.responsible_id || null
      };
      await axios.put(`/api/projects/${selectedProject.id}/tasks/${editingTask.id}`, payload, { headers: headers() });
      setSuccess('Задача обновлена');
      setShowTaskModal(false);
      setEditingTask(null);
      setTaskForm({ name: '', description: '', status: 'todo', priority: 'medium', progress: 0, estimated_hours: 0, actual_hours: 0, budget_planned: 0, actual_cost: 0, responsible_id: null, parent_id: null, start_date: '', end_date: '' });
      await fetchProjectData(selectedProject.id);
      const list = await fetchProjects();
      if (selectedProject && list.length) setSelectedProject(list.find(p => p.id === selectedProject.id) || selectedProject);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join('; ') : 'Ошибка обновления'));
    }
  };

  const deleteTask = async () => {
    if (!selectedProject || !deleteTaskConfirm) return;
    try {
      await axios.delete(`/api/projects/${selectedProject.id}/tasks/${deleteTaskConfirm.id}`, { headers: headers() });
      setSuccess('Задача удалена');
      setDeleteTaskConfirm(null);
      await fetchProjectData(selectedProject.id);
      const list = await fetchProjects();
      if (selectedProject && list.length) setSelectedProject(list.find(p => p.id === selectedProject.id) || selectedProject);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    if (!selectedProject) return;
    try {
      await axios.put(`/api/projects/${selectedProject.id}/tasks/${taskId}`, { status: newStatus }, { headers: headers() });
      setSuccess('Статус обновлён');
      fetchProjectData(selectedProject.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка');
    }
  };

  const moveTaskParent = async (taskId, newParentId) => {
    if (!selectedProject) return;
    try {
      const params = { parent_id: (newParentId === null || newParentId === 0 || !newParentId) ? 0 : newParentId };
      await axios.put(`/api/projects/${selectedProject.id}/tasks/${taskId}/hierarchy`, null, { params, headers: headers() });
      setSuccess('Иерархия обновлена');
      fetchProjectData(selectedProject.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка');
    }
  };

  const createClient = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/crm/clients', clientForm, { headers: headers() });
      setSuccess('Клиент добавлен');
      setShowClientModal(false);
      setClientForm({ name: '', contact_person: '', email: '', phone: '', address: '', notes: '' });
      setEditingClient(null);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка');
    }
  };

  const updateClient = async (e) => {
    e.preventDefault();
    if (!editingClient) return;
    try {
      await axios.put(`/api/crm/clients/${editingClient.id}`, clientForm, { headers: headers() });
      setSuccess('Клиент обновлён');
      setShowClientModal(false);
      setEditingClient(null);
      setClientForm({ name: '', contact_person: '', email: '', phone: '', address: '', notes: '' });
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка');
    }
  };

  const deleteClient = async (client) => {
    try {
      await axios.delete(`/api/crm/clients/${client.id}`, { headers: headers() });
      setSuccess('Клиент удалён');
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const createRisk = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;
    try {
      await axios.post(`/api/projects/${selectedProject.id}/risks`, riskForm, { headers: headers() });
      setSuccess('Риск добавлен');
      setShowRiskModal(false);
      setEditingRisk(null);
      setRiskForm({ title: '', description: '', probability: 50, impact: 50, mitigation: '', status: 'open' });
      fetchProjectData(selectedProject.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка добавления');
    }
  };

  const updateRisk = async (e) => {
    e.preventDefault();
    if (!selectedProject || !editingRisk) return;
    try {
      await axios.put(`/api/projects/${selectedProject.id}/risks/${editingRisk.id}`, riskForm, { headers: headers() });
      setSuccess('Риск обновлён');
      setShowRiskModal(false);
      setEditingRisk(null);
      setRiskForm({ title: '', description: '', probability: 50, impact: 50, mitigation: '', status: 'open' });
      fetchProjectData(selectedProject.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка');
    }
  };

  const deleteRisk = async () => {
    if (!selectedProject || !deleteRiskConfirm) return;
    try {
      await axios.delete(`/api/projects/${selectedProject.id}/risks/${deleteRiskConfirm.id}`, { headers: headers() });
      setSuccess('Риск удалён');
      setDeleteRiskConfirm(null);
      fetchProjectData(selectedProject.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!selectedProject || !commentText.trim()) return;
    try {
      await axios.post(`/api/projects/${selectedProject.id}/comments`, { content: commentText, task_id: commentTaskId || null }, { headers: headers() });
      setSuccess('Комментарий добавлен. Участникам задачи отправлено уведомление в Telegram.');
      setCommentText('');
      setCommentTaskId(null);
      fetchProjectData(selectedProject.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка');
    }
  };

  const flattenTasks = (items) => {
    const out = [];
    const walk = (list) => {
      (list || []).forEach(({ task, children }) => { out.push(task); walk(children); });
    };
    walk(items);
    return out;
  };

  const formatSize = (bytes) => bytes < 1024 ? bytes + ' Б' : bytes < 1024 * 1024 ? (bytes / 1024).toFixed(1) + ' КБ' : bytes < 1024 * 1024 * 1024 ? (bytes / (1024 * 1024)).toFixed(1) + ' МБ' : (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' ГБ';
  const uploadFile = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !selectedProject) return;
    setProjectFileUploadName(file.name);
    setProjectFileUploadSize(file.size);
    setProjectFileUploadProgress(0);
    setProjectFileUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`/api/projects/${selectedProject.id}/upload`, fd, {
        headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => { if (ev.total) setProjectFileUploadProgress(Math.round((ev.loaded / ev.total) * 100)); }
      });
      setSuccess('Файл загружен');
      fetchProjectData(selectedProject.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка загрузки');
    } finally {
      setProjectFileUploading(false);
      setProjectFileUploadProgress(0);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const renderTaskTree = (items, level = 0) => {
    const flat = flattenTasks(tasks);
    return items.map(({ task, children }) => (
      <div key={task.id} style={{ marginLeft: level * 20, marginBottom: 8 }}>
        <div className="wiki-article-card" style={{ padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <strong>{task.wbs_code || task.id}. {task.name}</strong>
            <span style={{ marginLeft: 8 }} className={`badge ${task.status === 'done' ? 'badge-active' : 'badge-inactive'}`}>{task.status}</span>
            <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-secondary)' }}>{task.progress}%</span>
            {task.start_date && <span style={{ marginLeft: 8, fontSize: 11 }}>{formatDate(task.start_date)}—{formatDate(task.end_date)}</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="btn-secondary btn-small" style={{ padding: '6px 10px', fontSize: 13 }} onClick={async () => { setEditingTask(task); setTaskForm({ name: task.name, description: task.description || '', status: task.status, priority: task.priority, progress: task.progress || 0, estimated_hours: task.estimated_hours || 0, actual_hours: task.actual_hours || 0, budget_planned: task.budget_planned || 0, actual_cost: task.actual_cost || 0, responsible_id: task.responsible_id, parent_id: task.parent_id || null, start_date: task.start_date ? task.start_date.slice(0,10) : '', end_date: task.end_date ? task.end_date.slice(0,10) : '' }); setShowTaskModal(true); try { const [teRes, depRes, allocRes] = await Promise.all([axios.get(`/api/projects/${selectedProject.id}/tasks/${task.id}/time-entries`, { headers: headers() }), axios.get(`/api/projects/${selectedProject.id}/tasks/${task.id}/dependencies`, { headers: headers() }), axios.get(`/api/projects/${selectedProject.id}/tasks/${task.id}/resource-allocations`, { headers: headers() })]); setTimeEntries(teRes.data.time_entries || []); setTaskDependencies(depRes.data.dependencies || []); setResourceAllocations(allocRes.data.allocations || []); } catch { setTimeEntries([]); setTaskDependencies([]); setResourceAllocations([]); } }}>✏️</button>
            <div className="form-group" style={{ marginBottom: 0, flex: '1 1 140px', minWidth: 120, maxWidth: 220 }}>
              <select defaultValue="" onChange={(e) => { const v = e.target.value; if (v === '') return; moveTaskParent(task.id, v === 'root' ? 0 : parseInt(v)); e.target.value = ''; }} title="Перенести задачу в другую ветку иерархии">
                <option value="">Перенести в...</option>
                <option value="root">Корень</option>
                {flat.filter(x => x.id !== task.id && !(task.parent_id && x.id === task.parent_id)).map(x => (
                  <option key={x.id} value={x.id}>{x.wbs_code} {x.name}</option>
                ))}
              </select>
            </div>
            <button type="button" className="btn-danger btn-small" style={{ padding: '6px 10px', fontSize: 13 }} onClick={() => setDeleteTaskConfirm(task)}>🗑️</button>
          </div>
        </div>
        {children?.length ? renderTaskTree(children, level + 1) : null}
      </div>
    ));
  };

  const renderGanttBars = () => {
    if (!ganttData?.tasks?.length) return <p className="no-data">Нет задач для диаграммы</p>;
    const proj = ganttData.project || {};
    let minDate = proj.start_date ? new Date(proj.start_date) : null;
    let maxDate = proj.end_date ? new Date(proj.end_date) : null;
    ganttData.tasks.forEach(t => {
      const sd = t.start_date ? new Date(t.start_date) : null;
      const ed = t.end_date ? new Date(t.end_date) : null;
      if (sd && (!minDate || sd < minDate)) minDate = sd;
      if (ed && (!maxDate || ed > maxDate)) maxDate = ed;
    });
    if (!minDate) minDate = new Date();
    if (!maxDate || maxDate <= minDate) maxDate = new Date(minDate.getTime() + 30 * 86400000);
    const rangeDays = Math.max(1, (maxDate - minDate) / 86400000);
    const totalDays = Math.ceil(rangeDays);
    const formatDt = (d) => d ? new Date(d).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' }) : '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDateNorm = new Date(minDate);
    minDateNorm.setHours(0, 0, 0, 0);
    const todayOffsetDays = (today - minDateNorm) / 86400000;
    const showTodayLine = todayOffsetDays >= 0 && todayOffsetDays <= rangeDays;
    const todayLeftPct = showTodayLine ? (todayOffsetDays / rangeDays) * 100 : 0;
    const labelStep = rangeDays <= 14 ? 1 : rangeDays <= 60 ? 7 : Math.max(1, Math.floor(rangeDays / 12));
    return (
      <div className="gantt-container gantt-responsive" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <div style={{ marginBottom: 8, fontSize: 'clamp(11px, 2vw, 13px)', color: 'var(--text-secondary)' }}>Временная шкала: {formatDt(proj.start_date)} — {formatDt(proj.end_date)}</div>
        <div className="gantt-chart-row" style={{ display: 'flex', width: '100%', minWidth: 0 }}>
          <div className="gantt-col-label" style={{ minWidth: 'clamp(40px, 8vw, 56px)', flexShrink: 0, paddingRight: 8 }}>
            <div style={{ height: 28, display: 'flex', alignItems: 'center', fontSize: 'clamp(10px, 1.8vw, 12px)', color: 'var(--text-secondary)', fontWeight: 600 }}>№</div>
            {ganttData.tasks.map(t => (
              <div key={t.id} style={{ height: 28, marginBottom: 6, display: 'flex', alignItems: 'center', fontSize: 'clamp(12px, 2vw, 14px)', fontWeight: 500 }} title={t.name}>{t.wbs_code || t.id}</div>
            ))}
          </div>
          <div className="gantt-col-timeline" style={{ flex: 1, minWidth: 0, position: 'relative' }} onDragOver={(ev) => ev.preventDefault()} onDrop={async (ev) => { ev.preventDefault(); const taskId = ev.dataTransfer.getData('ganttTaskId'); if (!taskId || !selectedProject) return; const rect = ev.currentTarget.getBoundingClientRect(); const x = ev.clientX - rect.left; const pct = Math.max(0, Math.min(1, x / rect.width)); const dropDay = pct * rangeDays; const newStart = new Date(minDate.getTime() + dropDay * 86400000); const task = ganttData.tasks.find(t => String(t.id) === taskId); if (!task) return; const oldStart = task.start_date ? new Date(task.start_date) : newStart; const oldEnd = task.end_date ? new Date(task.end_date) : new Date(oldStart.getTime() + 86400000); const durationMs = oldEnd - oldStart; const newEnd = new Date(newStart.getTime() + durationMs); try { await axios.put(`/api/projects/${selectedProject.id}/tasks/${taskId}`, { start_date: newStart.toISOString().slice(0,10), end_date: newEnd.toISOString().slice(0,10) }, { headers: headers() }); setSuccess('Даты задачи обновлены'); fetchProjectData(selectedProject.id); } catch (err) { setError(err.response?.data?.detail || 'Ошибка'); } }}>
            {showTodayLine && (
              <div className="gantt-today-line" style={{ position: 'absolute', left: todayLeftPct + '%', top: 0, bottom: 0, width: 2, background: 'var(--danger-color)', zIndex: 3, pointerEvents: 'none' }} title="Сегодня" />
            )}
            <div className="gantt-scale-row" style={{ display: 'flex', width: '100%', height: 28, marginBottom: 6, alignItems: 'center', fontSize: 'clamp(9px, 1.5vw, 11px)', color: 'var(--text-secondary)' }}>
              {Array.from({ length: totalDays + 1 }, (_, i) => (
                <div key={i} className="gantt-scale-cell" style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>{i % labelStep === 0 ? formatDt(new Date(minDate.getTime() + i * 86400000)) : ''}</div>
              ))}
            </div>
            {ganttData.tasks.map(t => {
              const startDays = t.start_date ? (new Date(t.start_date) - minDate) / 86400000 : 0;
              const endDays = t.end_date ? (new Date(t.end_date) - minDate) / 86400000 : startDays + 1;
              const durationDays = Math.max(0.25, endDays - startDays);
              const leftPct = (startDays / rangeDays) * 100;
              const widthPct = (durationDays / rangeDays) * 100;
              const isCritical = criticalPath && criticalPath.critical_path_ids && criticalPath.critical_path_ids.includes(t.id);
              return (
                <div key={t.id} style={{ height: 28, marginBottom: 6, position: 'relative', width: '100%' }}>
                  <div style={{ position: 'absolute', left: 0, right: 0, height: 22, background: 'var(--darker-bg)', borderRadius: 4 }} />
                  <div draggable onDragStart={(ev) => ev.dataTransfer.setData('ganttTaskId', String(t.id))} onDragEnd={(ev) => ev.dataTransfer.clearData()} style={{ position: 'absolute', left: leftPct + '%', width: widthPct + '%', height: 22, background: isCritical ? 'linear-gradient(90deg, #ef4444, #dc2626)' : 'linear-gradient(90deg, var(--primary-color), var(--secondary-color))', borderRadius: 4, boxShadow: isCritical ? '0 0 0 2px rgba(239,68,68,0.5)' : '0 1px 3px rgba(0,0,0,0.12)', cursor: 'grab', border: isCritical ? '2px solid #ef4444' : 'none' }} title={`${t.wbs_code} ${t.name} — ${formatDt(t.start_date)}–${formatDt(t.end_date)} (${t.progress}%)${isCritical ? ' [Критический путь]' : ''}`} />
                </div>
              );
            })}
          </div>
          <div className="gantt-col-progress" style={{ minWidth: 'clamp(36px, 6vw, 48px)', flexShrink: 0, paddingLeft: 8 }}>
            <div style={{ height: 28, marginBottom: 6, display: 'flex', alignItems: 'center', fontSize: 'clamp(10px, 1.8vw, 12px)', color: 'var(--text-secondary)' }}>%</div>
            {ganttData.tasks.map(t => (
              <div key={t.id} style={{ height: 28, marginBottom: 6, display: 'flex', alignItems: 'center', fontSize: 'clamp(11px, 1.8vw, 13px)', color: 'var(--text-secondary)' }}>{t.progress}%</div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="project-management">
      <div className="content-header wiki-header">
        <div>
          <h1 className="wiki-title">Управление проектами</h1>
          <p className="wiki-subtitle">Планирование (WBS, Ганта), ресурсы, риски, коммуникация, аналитика</p>
        </div>
        <button className="btn-success btn-small" onClick={() => { setEditingProject(null); setProjectForm({ name: '', description: '', status: 'active', priority: 'medium', organization_id: null, responsible_id: null, client_id: null, start_date: '', end_date: '', budget_planned: '' }); setShowProjectModal(true); }}>
          + Создать проект
        </button>
      </div>

      <div className="subsection-tabs" style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {['projects', 'planning', 'kanban', 'resources', 'risks', 'communication', 'vks', 'analytics', 'crm', 'catalog'].map(tab => (
          <button key={tab} type="button" className={`btn-small ${activeTab === tab ? 'btn-success' : 'btn-secondary'}`} onClick={() => setActiveTab(tab)}>
            {tab === 'projects' && 'Проекты'}
            {tab === 'planning' && 'Планирование (WBS/Ганта)'}
            {tab === 'kanban' && 'Канбан'}
            {tab === 'resources' && 'Ресурсы'}
            {tab === 'risks' && 'Риски'}
            {tab === 'communication' && 'Коммуникация'}
            {tab === 'vks' && 'ВКС'}
            {tab === 'analytics' && 'Аналитика'}
            {tab === 'crm' && 'CRM (Клиенты)'}
            {tab === 'catalog' && 'Оборудование/Материалы'}
          </button>
        ))}
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {activeTab === 'projects' && (
        <div>
          {loading ? <div className="loading">Загрузка...</div> : (
            <div className="wiki-articles-grid">
              {projects.map(p => (
                <div key={p.id} className={`wiki-article-card ${selectedProject?.id === p.id ? 'wiki-article-card-selected' : ''}`} style={{ position: 'relative' }}>
                  <div onClick={() => setSelectedProject(p)} style={{ cursor: 'pointer' }}>
                    <div className="wiki-card-icon">🎯</div>
                    <div className="wiki-card-title">{p.name}</div>
                    <div className="wiki-card-meta">Статус: {p.status} | Приоритет: {p.priority}</div>
                    <div className="wiki-card-meta">{formatDate(p.start_date)} — {formatDate(p.end_date)}</div>
                    {((p.budget_planned != null && p.budget_planned > 0) || (p.budget_spent != null && p.budget_spent > 0)) ? (() => { const plan = p.budget_planned || 0; const fact = p.budget_spent || 0; let cls = 'budget-green'; if (plan > 0) { const ratio = fact / plan; if (ratio >= 1) cls = 'budget-red'; else if (ratio >= 0.8) cls = 'budget-yellow'; } else if (fact > 0) cls = 'budget-red'; return <div className={`wiki-card-meta ${cls}`}>Бюджет: план {(plan).toLocaleString('ru-RU')} ₽, факт {(fact).toLocaleString('ru-RU')} ₽</div>; })() : null}
                    {p.organization_name && <div className="wiki-card-meta">Организация: {p.organization_name}</div>}
                    {p.created_by_name && <div className="wiki-card-meta">Ответственный: {p.created_by_name}</div>}
                    {p.executor_names && p.executor_names.length > 0 && <div className="wiki-card-meta">Исполнители: {p.executor_names.slice(0, 3).join(', ')}{p.executor_names.length > 3 ? '…' : ''}</div>}
                    {clients.find(c => c.id === p.client_id) && <div className="wiki-card-meta">Клиент: {clients.find(c => c.id === p.client_id).name}</div>}
                  </div>
                  <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 4 }}>
                    <button type="button" className="btn-secondary btn-small" style={{ padding: '4px 8px', fontSize: 12 }} onClick={(e) => { e.stopPropagation(); setEditingProject(p); setProjectForm({ name: p.name, description: p.description || '', status: p.status, priority: p.priority, organization_id: p.organization_id || null, responsible_id: p.responsible_id || null, client_id: p.client_id, start_date: p.start_date ? String(p.start_date).slice(0,10) : '', end_date: p.end_date ? String(p.end_date).slice(0,10) : '', budget_planned: p.budget_planned != null ? String(p.budget_planned) : '' }); setShowProjectModal(true); }}>✏️</button>
                    {currentUser?.is_admin && <button type="button" className="btn-danger btn-small" style={{ padding: '4px 8px', fontSize: 12 }} onClick={(e) => { e.stopPropagation(); setDeleteProjectConfirm(p); }} title="Удалить проект (только администратор)">🗑️</button>}
                  </div>
                </div>
              ))}
              {projects.length === 0 && <div className="no-data">Нет проектов. Создайте первый проект.</div>}
            </div>
          )}
        </div>
      )}

      {activeTab === 'planning' && selectedProject && (
        <div>
          <div className="content-header" style={{ marginBottom: 16 }}>
            <h2>Планирование: {selectedProject.name}</h2>
            <button className="btn-success btn-small" onClick={() => { setEditingTask(null); setTaskForm({ name: '', description: '', status: 'todo', priority: 'medium', progress: 0, estimated_hours: 0, actual_hours: 0, budget_planned: 0, actual_cost: 0, responsible_id: null, parent_id: null, start_date: '', end_date: '' }); setShowTaskModal(true); }}>+ Добавить задачу</button>
          </div>
          {criticalPath && criticalPath.critical_path_ids && criticalPath.critical_path_ids.length > 0 && (
            <div style={{ marginBottom: 24, padding: 16, background: 'var(--card-bg)', borderRadius: 12, border: '1px solid var(--border-color)' }}>
              <h3>Критический путь (CPM)</h3>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 12 }}>Длительность проекта: {criticalPath.project_duration_days || 0} дн.</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {(criticalPath.tasks || []).filter(t => t.is_critical).map(t => (
                  <span key={t.id} style={{ padding: '6px 12px', background: 'rgba(234,67,53,0.2)', borderRadius: 8, fontSize: 14 }}>{t.wbs_code} {t.name}</span>
                ))}
              </div>
            </div>
          )}
          <div style={{ marginBottom: 24 }}>
            <h3>Диаграмма Ганта</h3>
            {renderGanttBars()}
          </div>
          <div>
            <h3>WBS — иерархия задач</h3>
            {tasks.length ? renderTaskTree(tasks) : <p className="no-data">Нет задач. Добавьте задачи.</p>}
          </div>
        </div>
      )}

      {activeTab === 'kanban' && selectedProject && (
        <div>
          <div className="content-header" style={{ marginBottom: 16 }}>
            <h2>Канбан: {selectedProject.name}</h2>
            <button className="btn-success btn-small" onClick={() => { setEditingTask(null); setTaskForm({ name: '', description: '', status: 'todo', priority: 'medium', progress: 0, estimated_hours: 0, actual_hours: 0, budget_planned: 0, actual_cost: 0, responsible_id: null, parent_id: null, start_date: '', end_date: '' }); setShowTaskModal(true); }}>+ Добавить задачу</button>
          </div>
          <div className="kanban-board" style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 16 }}>
            {['todo', 'in_progress', 'review', 'done'].map(st => (
              <div key={st} className="kanban-column" style={{ minWidth: 260, flex: '0 0 260px', background: 'var(--card-bg)', borderRadius: 12, border: '1px solid var(--border-color)', padding: 12 }}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.background = 'var(--darker-bg)'; }}
                onDragLeave={(e) => { e.currentTarget.style.background = 'var(--card-bg)'; }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.background = 'var(--card-bg)';
                  const tid = parseInt(e.dataTransfer.getData('taskId'), 10);
                  if (tid && tid > 0) { updateTaskStatus(tid, st); }
                }}>
                <h4 style={{ marginBottom: 12, fontSize: 14 }}>{st === 'todo' ? 'К выполнению' : st === 'in_progress' ? 'В работе' : st === 'review' ? 'На проверке' : 'Выполнено'}</h4>
                {(flattenTasks(tasks).filter(t => t.status === st) || []).map(task => (
                  <div key={task.id} className="kanban-card wiki-article-card" draggable onDragStart={(ev) => ev.dataTransfer.setData('taskId', String(task.id))}
                    style={{ marginBottom: 8, padding: 10, cursor: 'grab' }}
                    onClick={async () => { setEditingTask(task); setTaskForm({ name: task.name, description: task.description || '', status: task.status, priority: task.priority, progress: task.progress || 0, estimated_hours: task.estimated_hours || 0, actual_hours: task.actual_hours || 0, budget_planned: task.budget_planned || 0, actual_cost: task.actual_cost || 0, responsible_id: task.responsible_id, parent_id: task.parent_id || null, start_date: task.start_date ? task.start_date.slice(0,10) : '', end_date: task.end_date ? task.end_date.slice(0,10) : '' }); setShowTaskModal(true); try { const [teRes, depRes, allocRes] = await Promise.all([axios.get(`/api/projects/${selectedProject.id}/tasks/${task.id}/time-entries`, { headers: headers() }), axios.get(`/api/projects/${selectedProject.id}/tasks/${task.id}/dependencies`, { headers: headers() }), axios.get(`/api/projects/${selectedProject.id}/tasks/${task.id}/resource-allocations`, { headers: headers() })]); setTimeEntries(teRes.data.time_entries || []); setTaskDependencies(depRes.data.dependencies || []); setResourceAllocations(allocRes.data.allocations || []); } catch { setTimeEntries([]); setTaskDependencies([]); setResourceAllocations([]); } }}>
                    <strong>{task.wbs_code || task.id}. {task.name}</strong>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{task.progress}% · {task.estimated_hours || 0} ч</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'resources' && selectedProject && (
        <div>
          <div className="content-header" style={{ marginBottom: 16 }}>
            <h2>Ресурсы: {selectedProject.name}</h2>
          </div>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>Участники проекта, оборудование и материалы по назначениям в задачах.</p>
          <div className="project-resources-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div>
              <h3>Участники проекта</h3>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>Формируются автоматически: ответственный за проект и ответственные по задачам.</p>
              {members.map((m, idx) => (
                <div key={(m.task_id || 0) + '-' + m.user_id + '-' + idx} className="wiki-article-card" style={{ marginBottom: 8, padding: 12 }}>
                  {m.user_name || users.find(u => u.id === m.user_id)?.full_name || users.find(u => u.id === m.user_id)?.username || 'ID:' + m.user_id} — {m.role}
                </div>
              ))}
              {members.length === 0 && <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Назначьте ответственного за проект в карточке проекта и ответственных по задачам в планировании.</p>}
            </div>
            <div>
              <h3>Оборудование из задач</h3>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>Автоматически из назначений ресурсов в задачах проекта. Единица измерения задаётся при создании оборудования в каталоге.</p>
              {projectResourceSummary.equipment && projectResourceSummary.equipment.length > 0 ? projectResourceSummary.equipment.map(e => (
                <div key={'eq-' + e.id} className="wiki-article-card" style={{ marginBottom: 8, padding: 12 }}>
                  <strong>{e.name}</strong> — всего: {e.total_quantity} {(e.unit || 'шт').trim()}{e.total_hours ? `, ${e.total_hours} ч` : ''}
                  {e.tasks && e.tasks.length > 0 && (
                    <ul style={{ marginTop: 8, paddingLeft: 20, fontSize: 13, color: 'var(--text-secondary)' }}>
                      {e.tasks.map((t, i) => <li key={i}>{t.task_name}: {t.quantity} {(t.unit || e.unit || 'шт').trim()}{t.hours ? ` (${t.hours} ч)` : ''}</li>)}
                    </ul>
                  )}
                </div>
              )) : <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Нет назначений. Добавьте оборудование в задачах проекта.</p>}
            </div>
          </div>
          <div className="project-resources-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 24, marginTop: 24 }}>
            <div>
              <h3>Материалы из задач</h3>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>Автоматически из назначений ресурсов в задачах проекта. Единица измерения задаётся при создании материала в каталоге.</p>
              {projectResourceSummary.materials && projectResourceSummary.materials.length > 0 ? projectResourceSummary.materials.map(m => (
                <div key={'mat-' + m.id} className="wiki-article-card" style={{ marginBottom: 8, padding: 12 }}>
                  <strong>{m.name}</strong> — всего: {m.total_quantity} {(m.unit || 'шт').trim()}
                  {m.tasks && m.tasks.length > 0 && (
                    <ul style={{ marginTop: 8, paddingLeft: 20, fontSize: 13, color: 'var(--text-secondary)' }}>
                      {m.tasks.map((t, i) => <li key={i}>{t.task_name}: {t.quantity} {(t.unit || m.unit || 'шт').trim()}</li>)}
                    </ul>
                  )}
                </div>
              )) : <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Нет назначений. Добавьте материалы в задачах проекта.</p>}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'risks' && selectedProject && (
        <div>
          <div className="content-header" style={{ marginBottom: 16 }}>
            <h2>Риски: {selectedProject.name}</h2>
            <button className="btn-success btn-small" onClick={() => { setEditingRisk(null); setRiskForm({ title: '', description: '', probability: 50, impact: 50, mitigation: '', status: 'open' }); setShowRiskModal(true); }}>+ Добавить риск</button>
          </div>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>Управление рисками проекта: вероятность, влияние, меры по снижению.</p>
          {risks.map(r => (
            <div key={r.id} className="wiki-article-card" style={{ marginBottom: 8, padding: 12, borderLeft: '4px solid var(--warning-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <div>
                <strong>{r.title}</strong> — вероятность: {r.probability}%, влияние: {r.impact}%{r.status ? `, статус: ${r.status}` : ''}
                {r.description && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>{r.description}</div>}
                {r.mitigation && <div style={{ fontSize: 13, marginTop: 4 }}>Меры: {r.mitigation}</div>}
              </div>
              <div style={{ flexShrink: 0 }}>
                <button type="button" className="btn-secondary btn-small" style={{ marginRight: 4 }} onClick={() => { setEditingRisk(r); setRiskForm({ title: r.title, description: r.description || '', probability: r.probability ?? 50, impact: r.impact ?? 50, mitigation: r.mitigation || '', status: r.status || 'open' }); setShowRiskModal(true); }}>✏️</button>
                <button type="button" className="btn-danger btn-small" onClick={() => setDeleteRiskConfirm(r)}>🗑️</button>
              </div>
            </div>
          ))}
          {risks.length === 0 && <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Нет рисков. Добавьте риск по кнопке выше.</p>}
        </div>
      )}

      {activeTab === 'communication' && selectedProject && (
        <div>
          <h2>Коммуникация: {selectedProject.name}</h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 12 }}>При отправке комментария по задаче — всем участникам задачи (ответственный, исполнители) отправляется уведомление в Telegram.</p>
          <form onSubmit={addComment} style={{ marginBottom: 24 }}>
            <div className="form-group">
              <label>Привязать к задаче (для уведомления участников)</label>
              <select value={commentTaskId || ''} onChange={(e) => setCommentTaskId(e.target.value ? parseInt(e.target.value) : null)} title="Задача для уведомления участников">
                <option value="">Общий комментарий по проекту</option>
                {flattenTasks(tasks).map(t => <option key={t.id} value={t.id}>{t.wbs_code} {t.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Текст сообщения</label>
              <textarea value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Введите комментарий..." rows={4} title="Текст сообщения для участников" />
            </div>
            <button type="submit" className="btn-success btn-small">Отправить</button>
          </form>
          <div style={{ marginBottom: 24 }}>
            <h3>Обсуждения</h3>
            {comments.map(c => (
              <div key={c.id} className="wiki-article-card" style={{ marginBottom: 8, padding: 12 }}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                  {formatDateTime(c.created_at)}
                  {c.author_name && <span> · {c.author_name}</span>}
                  {c.subject && <span> · {c.subject}</span>}
                </div>
                <div>{c.content}</div>
              </div>
            ))}
          </div>
          <div>
            <h3>Файлы проекта</h3>
            <div style={{ marginBottom: 12 }}>
              <button type="button" className="btn-success btn-small" onClick={() => fileInputRef.current?.click()} disabled={projectFileUploading}>
                {projectFileUploading ? 'Загрузка…' : 'Загрузить файл'}
              </button>
              <input type="file" ref={fileInputRef} onChange={uploadFile} style={{ display: 'none' }} />
            </div>
            {projectFileUploading && (
              <div className="nas-upload-progress-block" style={{ marginBottom: 16 }}>
                <div className="nas-upload-progress-header">
                  <span className="nas-upload-progress-name">{projectFileUploadName}</span>
                  <span className="nas-upload-progress-size">{formatSize(projectFileUploadSize)}</span>
                </div>
                <div className="nas-upload-progress-bar-wrap">
                  <div className="nas-upload-progress-bar" style={{ width: projectFileUploadProgress + '%' }} />
                </div>
                <div className="nas-upload-progress-percent">{projectFileUploadProgress}%</div>
              </div>
            )}
            {files.map(f => (
              <div key={f.id} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <button type="button" className="btn-secondary btn-small" onClick={async () => {
                  try {
                    const res = await axios.get(`/api/projects/${selectedProject.id}/files/${f.id}/download`, { headers: headers(), responseType: 'blob' });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = f.filename;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (err) { setError('Ошибка скачивания'); }
                }}>📥 {f.filename}</button>
                <button type="button" className="btn-danger btn-small" onClick={async () => {
                  if (!confirm('Удалить файл «' + f.filename + '» из проекта?')) return;
                  try {
                    await axios.delete(`/api/projects/${selectedProject.id}/files/${f.id}`, { headers: headers() });
                    setSuccess('Файл удалён');
                    fetchProjectData(selectedProject.id);
                  } catch (err) { setError(err.response?.data?.detail || 'Ошибка удаления'); }
                }}>🗑️ Удалить</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'vks' && selectedProject && (
        <div>
          <div className="content-header" style={{ marginBottom: 16 }}>
            <h2>ВКС — видеоконференции: {selectedProject.name}</h2>
            <button type="button" className="btn-success btn-small" onClick={() => { setEditingConference(null); setConferenceForm({ title: '', scheduled_at: '', duration_minutes: 60, participant_ids: [] }); setShowConferenceModal(true); }}>+ Создать ВКС</button>
          </div>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>Запланируйте видеоконференцию, выберите участников. Участники получат уведомления в Telegram. За 5 минут до начала — напоминание и ссылка. Записи после окончания ВКС копируются в каталог проекта на NAS и отображаются ниже.</p>

          <div style={{ marginBottom: 24, padding: 16, background: 'var(--darker-bg)', border: '1px solid var(--border-color)', borderRadius: 12 }}>
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>Записи ВКС по проекту</h3>
            {conferences.some(c => (conferenceRecordings[c.id] || []).length > 0) ? (
              conferences.map(conf => ((conferenceRecordings[conf.id] || []).length > 0) ? (
                <div key={conf.id} style={{ marginBottom: 16 }}>
                  <strong>{conf.title}</strong>
                  {(conferenceRecordings[conf.id] || []).map(rec => (
                    <div key={rec.id} style={{ marginLeft: 16, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <button type="button" className="btn-success btn-small" onClick={() => setPlayingVksRecording({ rec, conf })} title="Просмотр в плеере">▶ Смотреть</button>
                      <button type="button" className="btn-secondary btn-small" onClick={async () => {
                        try {
                          const res = await axios.get(`/api/projects/${selectedProject.id}/conferences/${conf.id}/recordings/${rec.id}/download`, { headers: headers(), responseType: 'blob' });
                          const url = URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = rec.filename || 'recording.mp4'; a.click(); URL.revokeObjectURL(url);
                        } catch (err) { setError('Ошибка скачивания'); }
                      }}>📥 Скачать</button>
                      <button type="button" className="btn-danger btn-small" onClick={async () => {
                        if (!confirm('Удалить запись «' + (rec.filename || '') + '»? Файлы будут удалены с NAS.')) return;
                        try {
                          await axios.delete(`/api/projects/${selectedProject.id}/conferences/${conf.id}/recordings/${rec.id}`, { headers: headers() });
                          setSuccess('Запись удалена');
                          if (playingVksRecording && playingVksRecording.rec.id === rec.id) setPlayingVksRecording(null);
                          setConferenceRecordings(prev => ({ ...prev, [conf.id]: (prev[conf.id] || []).filter(r => r.id !== rec.id) }));
                        } catch (err) { setError(err.response?.data?.detail || 'Ошибка удаления'); }
                      }} title="Удалить запись и файлы с NAS">🗑️ Удалить</button>
                      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{rec.filename} · {rec.file_size ? formatSize(rec.file_size) : ''} · {rec.created_at ? formatDateTime(rec.created_at) : ''}</span>
                    </div>
                  ))}
                </div>
              ) : null)
            ) : (
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>Записей пока нет. Записи появляются после окончания ВКС и автоматически копируются в каталог проекта на NAS (синхронизация каждую минуту).</p>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <h3 style={{ marginBottom: 8 }}>Запланированные ВКС</h3>
            {conferences.map(conf => (
              <div key={conf.id} className="wiki-article-card" style={{ padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <div>
                    <strong>{conf.title}</strong>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>{formatDateTime(conf.scheduled_at)} {conf.duration_minutes ? '(' + conf.duration_minutes + ' мин)' : ''} · Организатор: {conf.organizer_name || '—'}</div>
                    {conf.participants && conf.participants.length > 0 && <div style={{ fontSize: 12, marginTop: 4 }}>Участники: {conf.participants.map(p => p.user_name || 'ID:' + p.user_id).join(', ')}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" className="btn-success btn-small" onClick={() => openJoinVks(conf)}>Войти в ВКС</button>
                    <button type="button" className="btn-secondary btn-small" onClick={() => { setEditingConference(conf); setConferenceForm({ title: conf.title, scheduled_at: conf.scheduled_at ? conf.scheduled_at.slice(0, 16) : '', duration_minutes: conf.duration_minutes || 60, participant_ids: (conf.participants || []).map(p => p.user_id) }); setShowConferenceModal(true); }}>✏️</button>
                    <button type="button" className="btn-danger btn-small" onClick={() => setDeleteConferenceConfirm(conf)}>🗑️</button>
                  </div>
                </div>
              </div>
            ))}
            {conferences.length === 0 && <div className="no-data">Нет запланированных ВКС. Создайте конференцию.</div>}
          </div>
          {playingVksRecording && selectedProject && (() => {
            const token = localStorage.getItem('token');
            const streamUrl = token ? `/api/projects/${selectedProject.id}/conferences/${playingVksRecording.conf.id}/recordings/${playingVksRecording.rec.id}/stream?access_token=${encodeURIComponent(token)}` : null;
            return (
              <div className="vks-player-wrap" style={{ marginTop: 24, border: '1px solid var(--border-color)', borderRadius: 12, overflow: 'hidden', background: 'var(--darker-bg)' }}>
                <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, background: 'var(--card-bg)', borderBottom: '1px solid var(--border-color)' }}>
                  <strong>Просмотр записи: {playingVksRecording.rec.filename}</strong>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <label style={{ fontSize: 13 }}>Скорость: </label>
                    <select className="vks-speed-select" style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--darker-bg)', color: 'var(--text-primary)' }} onChange={e => { const v = vksPlayerRef.current; if (v) v.playbackRate = parseFloat(e.target.value); }} defaultValue="1">
                      {[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map(x => <option key={x} value={x}>{x === 1 ? '1 (норм.)' : x}</option>)}
                    </select>
                    <button type="button" className="btn-secondary btn-small" onClick={() => { const v = vksPlayerRef.current; if (v) v.requestPictureInPicture?.(); }} title="Картинка в картинке">🖼️ PiP</button>
                    <button type="button" className="btn-secondary btn-small" onClick={() => { const v = vksPlayerRef.current; if (v) { if (v.requestFullscreen) v.requestFullscreen(); else if (v.webkitRequestFullscreen) v.webkitRequestFullscreen(); } }} title="Полный экран">⛶ Полный экран</button>
                    <button type="button" className="btn-secondary btn-small" onClick={() => setPlayingVksRecording(null)}>Закрыть</button>
                  </div>
                </div>
                <div style={{ padding: 16, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360 }}>
                  {streamUrl ? (
                    <video ref={vksPlayerRef} src={streamUrl} controls controlsList="nodownload" style={{ maxWidth: '100%', width: '100%', maxHeight: '70vh', borderRadius: 8 }} crossOrigin="use-credentials" preload="metadata" playsInline>
                      Ваш браузер не поддерживает воспроизведение видео.
                    </video>
                  ) : (
                    <p style={{ color: 'var(--text-secondary)' }}>Войдите в систему для просмотра.</p>
                  )}
                </div>
              </div>
            );
          })()}
          {joinUrl && activeConferenceForJoin && (
            <div ref={vksContainerRef} style={{ marginTop: 24, border: '1px solid var(--border-color)', borderRadius: 12, overflow: 'hidden', background: 'var(--card-bg)', position: vksFullscreen ? 'fixed' : 'relative', top: vksFullscreen ? 0 : undefined, left: vksFullscreen ? 0 : undefined, right: vksFullscreen ? 0 : undefined, bottom: vksFullscreen ? 0 : undefined, zIndex: vksFullscreen ? 9999 : undefined, width: vksFullscreen ? '100vw' : '100%', height: vksFullscreen ? '100vh' : 560 }}>
              <div style={{ padding: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--surface)', borderBottom: '1px solid var(--border-color)' }}>
                <span><strong>ВКС:</strong> {activeConferenceForJoin.title}</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn-secondary btn-small" onClick={() => setVksFullscreen(!vksFullscreen)}>{vksFullscreen ? 'Свернуть' : 'На полный экран'}</button>
                  <button type="button" className="btn-secondary btn-small" onClick={() => { setJoinUrl(null); setActiveConferenceForJoin(null); }}>Закрыть</button>
                </div>
              </div>
              <iframe title="Jitsi Meet" src={joinUrl + '?userInfo.displayName=' + encodeURIComponent(currentUser?.full_name || currentUser?.username || 'User')} style={{ width: '100%', height: vksFullscreen ? 'calc(100vh - 48px)' : 504, border: 0 }} allow="camera; microphone; fullscreen; display-capture" />
            </div>
          )}
          {activeConferenceForJoin && (conferenceRecordings[activeConferenceForJoin.id] || []).length > 0 && (
            <div style={{ marginTop: 24 }}>
              <h3>Записи ВКС «{activeConferenceForJoin.title}»</h3>
              {(conferenceRecordings[activeConferenceForJoin.id] || []).map(rec => (
                <div key={rec.id} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="btn-success btn-small" onClick={() => setPlayingVksRecording({ rec, conf: activeConferenceForJoin })}>▶ Смотреть</button>
                  <button type="button" className="btn-secondary btn-small" onClick={async () => {
                    try {
                      const res = await axios.get(`/api/projects/${selectedProject.id}/conferences/${activeConferenceForJoin.id}/recordings/${rec.id}/download`, { headers: headers(), responseType: 'blob' });
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement('a'); a.href = url; a.download = rec.filename || 'recording.mp4'; a.click(); URL.revokeObjectURL(url);
                    } catch (err) { setError('Ошибка скачивания'); }
                  }}>📥 Скачать</button>
                  <button type="button" className="btn-danger btn-small" onClick={async () => {
                    if (!confirm('Удалить запись «' + (rec.filename || '') + '»? Файлы будут удалены с NAS.')) return;
                    try {
                      await axios.delete(`/api/projects/${selectedProject.id}/conferences/${activeConferenceForJoin.id}/recordings/${rec.id}`, { headers: headers() });
                      setSuccess('Запись удалена');
                      if (playingVksRecording && playingVksRecording.rec.id === rec.id) setPlayingVksRecording(null);
                      setConferenceRecordings(prev => ({ ...prev, [activeConferenceForJoin.id]: (prev[activeConferenceForJoin.id] || []).filter(r => r.id !== rec.id) }));
                    } catch (err) { setError(err.response?.data?.detail || 'Ошибка удаления'); }
                  }}>🗑️ Удалить</button>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{rec.filename} · {rec.file_size ? formatSize(rec.file_size) : ''} · {rec.created_at ? formatDateTime(rec.created_at) : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'crm' && (
        <div>
          <div className="content-header" style={{ marginBottom: 16 }}>
            <h2>CRM — Клиенты и сделки</h2>
            <button className="btn-success btn-small" onClick={() => { setShowClientModal(true); setEditingClient(null); setClientForm({ name: '', contact_person: '', email: '', phone: '', address: '', notes: '' }); }}>+ Добавить клиента</button>
          </div>
          <h3 style={{ marginBottom: 12 }}>Воронка сделок</h3>
          <div className="kanban-board" style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 16, marginBottom: 24 }}>
            {dealStages.map(st => (
              <div key={st.id} style={{ minWidth: 240, flex: '0 0 240px', background: 'var(--card-bg)', borderRadius: 12, border: '1px solid var(--border-color)', padding: 12 }}>
                <h4 style={{ marginBottom: 12, fontSize: 14 }}>{st.name}</h4>
                {(deals.filter(d => d.stage_id === st.id) || []).map(d => (
                  <div key={d.id} className="wiki-article-card" style={{ marginBottom: 8, padding: 10 }}>
                    <strong>{d.name}</strong>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{(d.amount || 0).toLocaleString('ru-RU')} ₽ · {d.probability}%</div>
                    <div style={{ marginTop: 8, display: 'flex', gap: 4 }}>
                      <button type="button" className="btn-secondary btn-small" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => { setEditingDeal(d); setDealForm({ name: d.name, client_id: d.client_id, stage_id: d.stage_id, project_id: d.project_id || null, amount: d.amount || 0, responsible_id: d.responsible_id || null }); setShowDealModal(true); }}>✏️</button>
                      <button type="button" className="btn-secondary btn-small" style={{ padding: '4px 8px', fontSize: 11 }} onClick={async () => { const newStageId = dealStages[Math.min(dealStages.findIndex(s => s.id === st.id) + 1, dealStages.length - 1)]?.id; if (newStageId) { try { await axios.put(`/api/crm/deals/${d.id}`, { stage_id: newStageId }, { headers: headers() }); fetchCatalogAndDeals(); } catch { setError('Ошибка'); } } }}>→</button>
                      <button type="button" className="btn-danger btn-small" style={{ padding: '4px 8px', fontSize: 11 }} onClick={async () => { try { await axios.delete(`/api/crm/deals/${d.id}`, { headers: headers() }); fetchCatalogAndDeals(); } catch { setError('Ошибка'); } }}>🗑️</button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <button className="btn-success btn-small" onClick={() => { if (!dealStages.length || !clients.length) { setError('Сначала добавьте этап сделки и клиента'); return; } setEditingDeal(null); setDealForm({ name: 'Новая сделка', client_id: clients[0]?.id || null, stage_id: dealStages[0]?.id || null, project_id: selectedProject?.id || null, amount: 0, responsible_id: null }); setShowDealModal(true); }}>+ Добавить сделку</button>
          <h3 style={{ marginTop: 24, marginBottom: 12 }}>Клиенты</h3>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>Клиенты привязываются к проектам при создании/редактировании проекта. Управление коммуникациями с клиентами.</p>
          {clients.length === 0 ? <div className="no-data">Нет клиентов. Добавьте первого клиента.</div> : (
            <div className="wiki-articles-grid">
              {clients.map(c => (
                <div key={c.id} className="wiki-article-card" style={{ position: 'relative' }}>
                  <div className="wiki-card-title">{c.name}</div>
                  {c.contact_person && <div className="wiki-card-meta">Контакт: {c.contact_person}</div>}
                  {c.email && <div className="wiki-card-meta">Email: {c.email}</div>}
                  {c.phone && <div className="wiki-card-meta">Тел: {c.phone}</div>}
                  <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                    <button type="button" className="btn-secondary btn-small" onClick={() => { setEditingClient(c); setClientForm({ name: c.name, contact_person: c.contact_person || '', email: c.email || '', phone: c.phone || '', address: c.address || '', notes: c.notes || '' }); setShowClientModal(true); }}>✏️ Редактировать</button>
                    <button type="button" className="btn-danger btn-small" onClick={() => deleteClient(c)}>🗑️ Удалить</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'catalog' && (
        <div>
          <div className="content-header" style={{ marginBottom: 16 }}>
            <h2>Единая база: Оборудование и материалы</h2>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 4 }}>Каталог используется при назначении ресурсов на задачи проектов.</p>
          </div>
          <div className="project-resources-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div>
              <h3>Оборудование</h3>
              <button className="btn-success btn-small" onClick={() => { setShowEquipmentModal(true); setEquipmentForm({ name: '', description: '', unit: 'шт' }); setEditingEquipment(null); }} style={{ marginBottom: 8 }}>+ Добавить</button>
              {catalogEquipment.map(e => (
                <div key={e.id} className="wiki-article-card" style={{ marginBottom: 8, padding: 12, display: 'flex', justifyContent: 'space-between' }}>
                  <span><strong>{e.name}</strong> {e.unit && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>({e.unit})</span>}</span>
                  <div><button type="button" className="btn-secondary btn-small" style={{ marginRight: 4 }} onClick={() => { setEditingEquipment(e); setEquipmentForm({ name: e.name, description: e.description || '', unit: e.unit || 'шт' }); setShowEquipmentModal(true); }}>✏️</button>
                  <button type="button" className="btn-danger btn-small" onClick={async () => { try { await axios.delete(`/api/catalog/equipment/${e.id}`, { headers: headers() }); fetchCatalogAndDeals(); } catch { setError('Ошибка'); } }}>🗑️</button></div>
                </div>
              ))}
              {catalogEquipment.length === 0 && <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Нет оборудования. Добавьте в каталог.</p>}
            </div>
            <div>
              <h3>Материалы</h3>
              <button className="btn-success btn-small" onClick={() => { setShowMaterialModal(true); setMaterialForm({ name: '', description: '', unit: 'шт' }); setEditingMaterial(null); }} style={{ marginBottom: 8 }}>+ Добавить</button>
              {catalogMaterials.map(m => (
                <div key={m.id} className="wiki-article-card" style={{ marginBottom: 8, padding: 12, display: 'flex', justifyContent: 'space-between' }}>
                  <span><strong>{m.name}</strong> {m.unit && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>({m.unit})</span>}</span>
                  <div><button type="button" className="btn-secondary btn-small" style={{ marginRight: 4 }} onClick={() => { setEditingMaterial(m); setMaterialForm({ name: m.name, description: m.description || '', unit: m.unit || 'шт' }); setShowMaterialModal(true); }}>✏️</button>
                  <button type="button" className="btn-danger btn-small" onClick={async () => { try { await axios.delete(`/api/catalog/materials/${m.id}`, { headers: headers() }); fetchCatalogAndDeals(); } catch { setError('Ошибка'); } }}>🗑️</button></div>
                </div>
              ))}
              {catalogMaterials.length === 0 && <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Нет материалов. Добавьте в каталог.</p>}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'analytics' && selectedProject && (
        <div className="analytics-dashboard">
          <div className="content-header" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
            <h2>BI-аналитика: {selectedProject.name}</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
              <label style={{ fontSize: 14, color: 'var(--text-secondary)', fontWeight: 500 }}>Период:</label>
              <div className="form-group" style={{ marginBottom: 0, flex: '0 0 auto' }}>
                <select value={analyticsPeriod} onChange={(e) => { setAnalyticsPeriod(e.target.value); }} title="Период для отчёта">
                  <option value="all">Всё время</option>
                  <option value="month">Последние 30 дней</option>
                  <option value="quarter">Последние 90 дней</option>
                  <option value="project">Сроки проекта</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0, flex: '0 0 auto' }}>
                <input type="date" value={analyticsDateFrom} onChange={(e) => setAnalyticsDateFrom(e.target.value)} placeholder="От" title="Начало периода для отчёта" style={{ maxWidth: 160 }} />
              </div>
              <div className="form-group" style={{ marginBottom: 0, flex: '0 0 auto' }}>
                <input type="date" value={analyticsDateTo} onChange={(e) => setAnalyticsDateTo(e.target.value)} placeholder="До" title="Конец периода для отчёта" style={{ maxWidth: 160 }} />
              </div>
              <button type="button" className="btn-primary btn-small" onClick={fetchAnalyticsOnly} disabled={analyticsLoading}>{analyticsLoading ? 'Загрузка…' : '🔄 Обновить'}</button>
              <button type="button" className="btn-success btn-small" onClick={async () => { try { const params = {}; if (analyticsDateFrom) params.date_from = analyticsDateFrom; if (analyticsDateTo) params.date_to = analyticsDateTo; const r = await axios.get(`/api/projects/${selectedProject.id}/export?format=xlsx`, { params, headers: headers(), responseType: 'blob' }); const u = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = u; a.download = `project_${selectedProject.id}_report.xlsx`; a.click(); URL.revokeObjectURL(u); setSuccess('Отчёт Excel скачан'); } catch (err) { setError('Ошибка экспорта'); } }}>📊 Excel</button>
              <button type="button" className="btn-secondary btn-small" onClick={async () => { try { const params = {}; if (analyticsDateFrom) params.date_from = analyticsDateFrom; if (analyticsDateTo) params.date_to = analyticsDateTo; const r = await axios.get(`/api/projects/${selectedProject.id}/export?format=pdf`, { params, headers: headers(), responseType: 'blob' }); const u = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = u; a.download = `project_${selectedProject.id}_report.pdf`; a.click(); URL.revokeObjectURL(u); setSuccess('Отчёт PDF скачан'); } catch (err) { setError('Ошибка экспорта'); } }}>📄 PDF</button>
            </div>
          </div>
          {!analytics && !analyticsLoading && <p style={{ color: 'var(--text-secondary)' }}>Выберите период и нажмите «Обновить» или переключите проект.</p>}
          {analytics && (
            <>
          <div className="system-stats" style={{ marginTop: 20, flexWrap: 'wrap', display: 'flex', gap: 12 }}>
            <div className="stat-card"><div className="stat-value">{analytics.tasks_total}</div><div className="stat-label">Всего задач</div></div>
            <div className="stat-card"><div className="stat-value">{analytics.tasks_done}</div><div className="stat-label">Выполнено</div></div>
            <div className="stat-card"><div className="stat-value">{analytics.completion_rate != null ? analytics.completion_rate + '%' : (analytics.tasks_total ? Math.round(analytics.tasks_done / analytics.tasks_total * 100) + '%' : '0%')}</div><div className="stat-label">% выполнения</div></div>
            <div className="stat-card"><div className="stat-value">{analytics.avg_progress}%</div><div className="stat-label">Средний прогресс</div></div>
            <div className="stat-card"><div className="stat-value">{(analytics.budget_planned || 0).toLocaleString('ru-RU')} ₽</div><div className="stat-label">Плановый бюджет</div></div>
            <div className="stat-card"><div className="stat-value">{(analytics.budget_spent || 0).toLocaleString('ru-RU')} ₽</div><div className="stat-label">Факт. затраты</div></div>
            <div className="stat-card"><div className={"stat-value " + ((analytics.budget_variance_percent || 0) > 0 ? "stat-variance-negative" : "")}>({analytics.budget_variance_percent != null ? analytics.budget_variance_percent + '%' : '—'})</div><div className="stat-label">Отклонение бюджета</div></div>
            <div className="stat-card"><div className="stat-value">{analytics.estimated_hours} ч</div><div className="stat-label">Оценка часов</div></div>
            <div className="stat-card"><div className="stat-value">{analytics.actual_hours} ч</div><div className="stat-label">Факт часов</div></div>
            <div className="stat-card"><div className={"stat-value " + ((analytics.hours_variance || 0) > 0 ? "stat-variance-negative" : "")}>{(analytics.hours_variance != null ? analytics.hours_variance : 0) > 0 ? '+' : ''}{(analytics.hours_variance != null ? analytics.hours_variance : 0)} ч</div><div className="stat-label">Отклонение часов</div></div>
            <div className="stat-card"><div className="stat-value">{analytics.open_risks}</div><div className="stat-label">Открытых рисков</div></div>
            <div className="stat-card"><div className="stat-value">{analytics.project_duration_days != null ? analytics.project_duration_days : '—'}</div><div className="stat-label">Крит. путь (дней)</div></div>
            {(analytics.cpi != null || analytics.spi != null) && (
              <>
                <div className="stat-card"><div className="stat-value">{analytics.cpi != null ? analytics.cpi : '—'}</div><div className="stat-label">CPI (EVA)</div></div>
                <div className="stat-card"><div className="stat-value">{analytics.spi != null ? analytics.spi : '—'}</div><div className="stat-label">SPI (EVA)</div></div>
                <div className="stat-card"><div className="stat-value">{analytics.eac != null ? (analytics.eac).toLocaleString('ru-RU') + ' ₽' : '—'}</div><div className="stat-label">EAC (прогноз)</div></div>
              </>
            )}
          </div>
          {(analytics.critical_path_tasks && analytics.critical_path_tasks.length > 0) && (
            <div className="analytics-chart-card" style={{ background: 'var(--card-bg)', borderRadius: 12, padding: 20, marginTop: 24, border: '1px solid var(--border-color)' }}>
              <h3 style={{ marginBottom: 12 }}>Критический путь</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {analytics.critical_path_tasks.map((ct, i) => (
                  <li key={ct.id} style={{ padding: '8px 0', borderBottom: i < analytics.critical_path_tasks.length - 1 ? '1px solid var(--border-color)' : 'none', display: 'flex', gap: 12 }}>
                    <span style={{ color: 'var(--text-secondary)', minWidth: 48 }}>{ct.wbs_code || '—'}</span>
                    <span>{ct.name}</span>
                    <span style={{ color: 'var(--text-secondary)', marginLeft: 'auto' }}>{ct.duration_days} дн.</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="analytics-charts" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 24, marginTop: 32 }}>
            {(analytics.tasks_by_status && analytics.tasks_by_status.length > 0) && (
              <div className="analytics-chart-card" style={{ background: 'var(--card-bg)', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <h3 style={{ marginBottom: 12 }}>Задачи по статусам</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={analytics.tasks_by_status.map(s => ({ name: s.status === 'todo' ? 'К выполнению' : s.status === 'in_progress' ? 'В работе' : s.status === 'review' ? 'На проверке' : 'Выполнено', value: s.count }))} cx="50%" cy="50%" outerRadius={85} label={({ name, value }) => name + ': ' + value} dataKey="value" isAnimationActive={true} animationDuration={600} animationBegin={0}>
                      {analytics.tasks_by_status.map((_, i) => <Cell key={i} fill={['#64748b', '#3b82f6', '#f59e0b', '#10b981'][i % 4]} stroke="var(--card-bg)" strokeWidth={1} />)}
                    </Pie>
                    <Tooltip formatter={(v) => [v, 'Задач']} contentStyle={{ borderRadius: 8 }} isAnimationActive={true} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="analytics-chart-card" style={{ background: 'var(--card-bg)', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <h3 style={{ marginBottom: 12 }}>Бюджет: план vs факт (₽)</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={[{ name: 'План', sum: analytics.budget_planned || 0, fill: '#3b82f6' }, { name: 'Факт', sum: analytics.budget_spent || 0, fill: '#f59e0b' }]} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                  <XAxis dataKey="name" tick={{ fill: 'var(--text-secondary)' }} />
                  <YAxis tick={{ fill: 'var(--text-secondary)' }} tickFormatter={(v) => v >= 1e6 ? (v/1e6).toFixed(1) + 'M' : v >= 1e3 ? (v/1e3).toFixed(0) + 'k' : v} />
                  <Tooltip formatter={(v) => [(v || 0).toLocaleString('ru-RU') + ' ₽', 'Сумма']} contentStyle={{ borderRadius: 8 }} />
                  <Legend />
                  <Bar dataKey="sum" name="Сумма" isAnimationActive={true} animationDuration={600} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="analytics-chart-card" style={{ background: 'var(--card-bg)', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <h3 style={{ marginBottom: 12 }}>Часы: оценка vs факт</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={[{ name: 'Оценка', hours: analytics.estimated_hours || 0, fill: '#8b5cf6' }, { name: 'Факт', hours: analytics.actual_hours || 0, fill: '#ec4899' }]} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                  <XAxis dataKey="name" tick={{ fill: 'var(--text-secondary)' }} />
                  <YAxis tick={{ fill: 'var(--text-secondary)' }} />
                  <Tooltip formatter={(v) => [v + ' ч', 'Часы']} contentStyle={{ borderRadius: 8 }} />
                  <Legend />
                  <Bar dataKey="hours" name="Часы" isAnimationActive={true} animationDuration={600} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {((analytics.progress_plan_timeline && analytics.progress_plan_timeline.length > 0) || (analytics.progress_timeline && analytics.progress_timeline.length > 0)) && (
              <div className="analytics-chart-card" style={{ background: 'var(--card-bg)', borderRadius: 12, padding: 16, gridColumn: '1 / -1', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <h3 style={{ marginBottom: 12 }}>Прогресс: план vs факт (%)</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={analytics.progress_plan_timeline && analytics.progress_plan_timeline.length ? analytics.progress_plan_timeline : analytics.progress_timeline} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis dataKey="date" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} tickFormatter={(v) => v && v.length >= 10 ? v.slice(0, 10) : v} />
                    <YAxis tick={{ fill: 'var(--text-secondary)' }} domain={[0, 100]} />
                    <Tooltip formatter={(v) => [v + '%', '']} labelFormatter={(l) => 'Дата: ' + l} contentStyle={{ borderRadius: 8 }} />
                    <Legend />
                    <Line type="monotone" dataKey="plan_progress" stroke="#6366f1" name="План %" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 5" isAnimationActive={true} animationDuration={700} />
                    <Line type="monotone" dataKey="fact_progress" stroke="#10b981" name="Факт %" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={true} animationDuration={700} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            {(analytics.risks_by_impact && analytics.risks_by_impact.length > 0) && (
              <div className="analytics-chart-card" style={{ background: 'var(--card-bg)', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <h3 style={{ marginBottom: 12 }}>Риски по влиянию</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={analytics.risks_by_impact} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis dataKey="label" tick={{ fill: 'var(--text-secondary)' }} />
                    <YAxis tick={{ fill: 'var(--text-secondary)' }} />
                    <Tooltip contentStyle={{ borderRadius: 8 }} />
                    <Bar dataKey="count" name="Количество" fill="#ef4444" isAnimationActive={true} animationDuration={600} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {(analytics.risks_matrix && analytics.risks_matrix.length > 0) && (
              <div className="analytics-chart-card" style={{ background: 'var(--card-bg)', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <h3 style={{ marginBottom: 12 }}>Матрица рисков (вероятность × влияние)</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={analytics.risks_matrix.map(r => ({ ...r, riskLabel: r.probability + ' / ' + r.impact }))} margin={{ top: 8, right: 8, left: 8, bottom: 8 }} layout="vertical" barCategoryGap="12%">
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis type="number" tick={{ fill: 'var(--text-secondary)' }} />
                    <YAxis type="category" dataKey="riskLabel" width={100} tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
                    <Tooltip formatter={(v) => [v, 'Рисков']} contentStyle={{ borderRadius: 8 }} />
                    <Bar dataKey="count" name="Количество" fill="#f97316" isAnimationActive={true} animationDuration={600} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {(analytics.top_risks && analytics.top_risks.length > 0) && (
              <div className="analytics-chart-card" style={{ background: 'var(--card-bg)', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <h3 style={{ marginBottom: 12 }}>Топ рисков по приоритету</h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: 260, overflowY: 'auto' }}>
                  {analytics.top_risks.slice(0, 8).map((r, i) => (
                    <li key={r.id} style={{ padding: '8px 0', borderBottom: i < 7 ? '1px solid var(--border-color)' : 'none' }}>
                      <span style={{ fontWeight: 500 }}>{r.title}</span>
                      <span style={{ color: 'var(--text-secondary)', fontSize: 12, marginLeft: 8 }}>P×I={r.priority}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(analytics.by_responsible && analytics.by_responsible.length > 0) && (
              <div className="analytics-chart-card" style={{ background: 'var(--card-bg)', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <h3 style={{ marginBottom: 12 }}>Нагрузка по ответственным</h3>
                <ResponsiveContainer width="100%" height={Math.min(320, 140 + analytics.by_responsible.length * 36)}>
                  <BarChart data={analytics.by_responsible.slice(0, 10)} layout="vertical" margin={{ top: 8, right: 8, left: 90, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis type="number" tick={{ fill: 'var(--text-secondary)' }} />
                    <YAxis type="category" dataKey="full_name" width={85} tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
                    <Tooltip formatter={(v, n) => (n === 'tasks_count' ? v + ' задач' : n === 'done_count' ? v + ' выполнено' : n === 'load_percent' ? v + '% загрузка' : v + ' ч')} contentStyle={{ borderRadius: 8 }} />
                    <Legend />
                    <Bar dataKey="tasks_count" fill="#3b82f6" name="Задач" stackId="a" isAnimationActive={true} animationDuration={600} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="actual_hours" fill="#f59e0b" name="Факт ч" isAnimationActive={true} animationDuration={600} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="load_percent" fill="#10b981" name="Загрузка %" isAnimationActive={true} animationDuration={600} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {(analytics.budget_by_tasks && analytics.budget_by_tasks.length > 0) && (
              <div className="analytics-chart-card" style={{ background: 'var(--card-bg)', borderRadius: 12, padding: 16, gridColumn: '1 / -1', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <h3 style={{ marginBottom: 12 }}>Бюджет по задачам (план / факт ₽)</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={analytics.budget_by_tasks.slice(0, 20)} margin={{ top: 8, right: 8, left: 8, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis dataKey="wbs_code" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} angle={-35} textAnchor="end" height={50} />
                    <YAxis tick={{ fill: 'var(--text-secondary)' }} tickFormatter={(v) => v >= 1e6 ? (v/1e6).toFixed(1) + 'M' : v >= 1e3 ? (v/1e3).toFixed(0) + 'k' : v} />
                    <Tooltip formatter={(v) => (v || 0).toLocaleString('ru-RU') + ' ₽'} contentStyle={{ borderRadius: 8 }} labelFormatter={(l, p) => p && p[0] && p[0].payload && p[0].payload.name} />
                    <Legend />
                    <Bar dataKey="budget_planned" name="План" fill="#3b82f6" isAnimationActive={true} animationDuration={600} radius={[4, 0, 0, 4]} />
                    <Bar dataKey="actual_cost" name="Факт" fill="#f59e0b" isAnimationActive={true} animationDuration={600} radius={[4, 0, 0, 4]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          <div style={{ marginTop: 32, padding: 20, background: 'var(--card-bg)', borderRadius: 12, border: '1px solid var(--border-color)' }}>
            <h3 style={{ marginBottom: 16 }}>Сравнение проектов (портфель)</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              {projects.filter(p => p.id !== selectedProject.id).slice(0, 15).map(p => (
                <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={portfolioProjectIds.includes(p.id)} onChange={(e) => setPortfolioProjectIds(prev => e.target.checked ? [...prev, p.id] : prev.filter(id => id !== p.id))} />
                  <span>{p.name}</span>
                </label>
              ))}
              <button type="button" className="btn-primary btn-small" onClick={fetchPortfolioAnalytics}>Сравнить</button>
            </div>
            {portfolioData && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                    <th style={{ textAlign: 'left', padding: 8 }}>Проект</th><th style={{ padding: 8 }}>Задач</th><th style={{ padding: 8 }}>Выполнено %</th><th style={{ padding: 8 }}>Бюджет план</th><th style={{ padding: 8 }}>Бюджет факт</th><th style={{ padding: 8 }}>Отклонение %</th><th style={{ padding: 8 }}>Рисков</th>
                  </tr></thead>
                  <tbody>
                    {portfolioData.projects.map(pr => (
                      <tr key={pr.project_id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: 8 }}>{pr.name}</td>
                        <td style={{ padding: 8, textAlign: 'center' }}>{pr.tasks_total}</td>
                        <td style={{ padding: 8, textAlign: 'center' }}>{pr.completion_rate}%</td>
                        <td style={{ padding: 8 }}>{(pr.budget_planned || 0).toLocaleString('ru-RU')} ₽</td>
                        <td style={{ padding: 8 }}>{(pr.budget_spent || 0).toLocaleString('ru-RU')} ₽</td>
                        <td style={{ padding: 8, textAlign: 'center', color: (pr.budget_variance_percent || 0) > 0 ? 'var(--danger)' : 'inherit' }}>{pr.budget_variance_percent}%</td>
                        <td style={{ padding: 8, textAlign: 'center' }}>{pr.open_risks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {portfolioData.summary && (
                  <p style={{ marginTop: 12, color: 'var(--text-secondary)', fontSize: 13 }}>Итого: задач {portfolioData.summary.total_tasks}, выполнено {portfolioData.summary.total_done} ({portfolioData.summary.total_completion_rate}%), бюджет план {(portfolioData.summary.total_budget_planned || 0).toLocaleString('ru-RU')} ₽, факт {(portfolioData.summary.total_budget_spent || 0).toLocaleString('ru-RU')} ₽, отклонение {portfolioData.summary.total_budget_variance_percent}%, рисков {portfolioData.summary.total_open_risks}.</p>
                )}
              </div>
            )}
          </div>
            </>
          )}
        </div>
      )}

      {!selectedProject && !['projects', 'crm', 'catalog'].includes(activeTab) && (
        <div className="placeholder-section">
          <div className="placeholder-icon">🎯</div>
          <p className="placeholder-description">Выберите проект в разделе «Проекты»</p>
        </div>
      )}

      {showProjectModal && (
        <div className="modal-overlay" onClick={() => { setShowProjectModal(false); setEditingProject(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editingProject ? 'Редактировать проект' : 'Создать проект'}</h2>
            <form onSubmit={editingProject ? updateProject : createProject}>
              <div className="form-group">
                <label>Название *</label>
                <input value={projectForm.name} onChange={e => setProjectForm({ ...projectForm, name: e.target.value })} required title="Название проекта" />
              </div>
              <div className="form-group">
                <label>Описание</label>
                <textarea value={projectForm.description} onChange={e => setProjectForm({ ...projectForm, description: e.target.value })} title="Описание целей и содержания проекта" />
              </div>
              <div className="form-group">
                <label>Статус</label>
                <select value={projectForm.status} onChange={e => setProjectForm({ ...projectForm, status: e.target.value })} title="Текущий статус проекта">
                  <option value="active">Активный</option>
                  <option value="completed">Завершён</option>
                  <option value="on_hold">Приостановлен</option>
                  <option value="cancelled">Отменён</option>
                </select>
              </div>
              <div className="form-group">
                <label>Приоритет</label>
                <select value={projectForm.priority} onChange={e => setProjectForm({ ...projectForm, priority: e.target.value })} title="Приоритет проекта">
                  <option value="low">Низкий</option>
                  <option value="medium">Средний</option>
                  <option value="high">Высокий</option>
                  <option value="critical">Критический</option>
                </select>
              </div>
              <div className="form-group">
                <label>Дата начала</label>
                <input type="date" value={projectForm.start_date} onChange={e => setProjectForm({ ...projectForm, start_date: e.target.value })} title="Планируемая дата начала проекта" />
              </div>
              <div className="form-group">
                <label>Дата окончания</label>
                <input type="date" value={projectForm.end_date} onChange={e => setProjectForm({ ...projectForm, end_date: e.target.value })} title="Планируемая дата окончания проекта" />
              </div>
              <div className="form-group">
                <label>Плановые затраты (руб)</label>
                <input type="number" min="0" step="1" value={projectForm.budget_planned} onChange={e => setProjectForm({ ...projectForm, budget_planned: e.target.value })} placeholder="Плановый бюджет проекта" title="Плановый бюджет проекта в рублях" />
              </div>
              <div className="form-group">
                <label>Организация (ответственная за проект)</label>
                <select value={projectForm.organization_id || ''} onChange={e => setProjectForm({ ...projectForm, organization_id: e.target.value ? parseInt(e.target.value) : null })} title="Организация-исполнитель">
                  <option value="">—</option>
                  {organizations.filter(o => o.is_active !== false).map(o => (
                    <option key={o.id} value={o.id}>{o.full_name || o.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Ответственный пользователь</label>
                <select value={projectForm.responsible_id || ''} onChange={e => setProjectForm({ ...projectForm, responsible_id: e.target.value ? parseInt(e.target.value) : null })} title="Руководитель проекта">
                  <option value="">—</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Клиент (CRM)</label>
                <select value={projectForm.client_id || ''} onChange={e => setProjectForm({ ...projectForm, client_id: e.target.value ? parseInt(e.target.value) : null })} title="Заказчик проекта">
                  <option value="">—</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-success">{editingProject ? 'Сохранить' : 'Создать'}</button>
                <button type="button" className="btn-secondary" onClick={() => { setShowProjectModal(false); setEditingProject(null); }}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showTaskModal && (
        <div className="modal-overlay" onClick={() => { setShowTaskModal(false); setEditingTask(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editingTask ? 'Редактировать задачу' : 'Добавить задачу'}</h2>
            <form onSubmit={editingTask ? updateTask : createTask}>
              <div className="form-group">
                <label>Название *</label>
                <input value={taskForm.name} onChange={e => setTaskForm({ ...taskForm, name: e.target.value })} required title="Название задачи" />
              </div>
              <div className="form-group">
                <label>Родительская задача</label>
                <select value={taskForm.parent_id || ''} onChange={e => setTaskForm({ ...taskForm, parent_id: e.target.value ? parseInt(e.target.value) : null })} title="Выберите родительскую задачу для иерархии">
                  <option value="">Корень (без родителя)</option>
                  {flattenTasks(tasks).filter(t => !editingTask || t.id !== editingTask.id).map(t => <option key={t.id} value={t.id}>{t.wbs_code} {t.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Дата начала</label>
                <input type="date" value={taskForm.start_date} onChange={e => setTaskForm({ ...taskForm, start_date: e.target.value })} title="Планируемая дата начала задачи" />
              </div>
              <div className="form-group">
                <label>Дата окончания</label>
                <input type="date" value={taskForm.end_date} onChange={e => setTaskForm({ ...taskForm, end_date: e.target.value })} title="Планируемый дедлайн задачи" />
              </div>
              <div className="form-group">
                <label>Статус</label>
                <select value={taskForm.status} onChange={e => setTaskForm({ ...taskForm, status: e.target.value })} title="Текущий статус задачи">
                  <option value="todo">К выполнению</option>
                  <option value="in_progress">В работе</option>
                  <option value="review">На проверке</option>
                  <option value="done">Выполнено</option>
                </select>
              </div>
              <div className="form-group">
                <label>Прогресс %</label>
                <input type="number" min="0" max="100" value={taskForm.progress} onChange={e => setTaskForm({ ...taskForm, progress: parseInt(e.target.value) || 0 })} title="Процент выполнения (0–100)" />
              </div>
              <div className="form-group">
                <label>Оценка часов</label>
                <input type="number" min="0" step="0.5" value={taskForm.estimated_hours} onChange={e => setTaskForm({ ...taskForm, estimated_hours: parseFloat(e.target.value) || 0 })} placeholder="Плановые трудозатраты" title="Плановые трудозатраты в часах" />
              </div>
              <div className="form-group">
                <label>Факт часов</label>
                <input type="number" min="0" step="0.25" value={taskForm.actual_hours} onChange={e => setTaskForm({ ...taskForm, actual_hours: parseFloat(e.target.value) || 0 })} placeholder="Сумма из трудозатрат или ручной ввод" title="Фактические трудозатраты (можно добавить через блок трудозатрат ниже)" />
              </div>
              <div className="form-group">
                <label>Плановые затраты по задаче (руб)</label>
                <input type="number" min="0" step="1" value={taskForm.budget_planned} onChange={e => setTaskForm({ ...taskForm, budget_planned: parseInt(e.target.value) || 0 })} title="Плановый бюджет задачи в рублях" />
              </div>
              <div className="form-group">
                <label>Фактические затраты (руб){taskForm.status === 'done' ? ' *' : ''}</label>
                <input type="number" min="0" step="1" value={taskForm.actual_cost} onChange={e => setTaskForm({ ...taskForm, actual_cost: parseInt(e.target.value) || 0 })} placeholder={taskForm.status === 'done' ? 'Укажите при закрытии задачи' : ''} title={taskForm.status === 'done' ? 'Обязательно укажите при закрытии задачи' : 'Фактические затраты по задаче'} />
              </div>
              <div className="form-group">
                <label>Трудозатраты</label>
                {editingTask ? (
                  <>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                        <input type="date" value={timeEntryDate || ''} onChange={e => setTimeEntryDate(e.target.value)} style={{ flex: '1 1 140px', minWidth: 0 }} title="Дата учёта трудозатрат" />
                        <input type="number" min="0.25" step="0.25" value={timeEntryHours} onChange={e => setTimeEntryHours(parseFloat(e.target.value) || 0)} placeholder="ч" style={{ width: 70 }} title="Количество часов" />
                        <button type="button" className="btn-success btn-small" onClick={async () => { try { await axios.post(`/api/projects/${selectedProject.id}/tasks/${editingTask.id}/time-entries`, { date: timeEntryDate || new Date().toISOString().slice(0,10), hours: timeEntryHours || 0.5, comment: timeEntryComment }, { headers: headers() }); setTimeEntryDate(''); setTimeEntryHours(0.5); setTimeEntryComment(''); const r = await axios.get(`/api/projects/${selectedProject.id}/tasks/${editingTask.id}/time-entries`, { headers: headers() }); setTimeEntries(r.data.time_entries || []); setTaskForm(f => ({ ...f, actual_hours: r.data.time_entries?.reduce((s,e) => s + (e.hours||0), 0) || f.actual_hours })); fetchProjectData(selectedProject.id); } catch (err) { setError(err.response?.data?.detail || 'Ошибка'); } }}>+ Добавить</button>
                      </div>
                      <textarea value={timeEntryComment} onChange={e => setTimeEntryComment(e.target.value)} placeholder="Комментарий к трудозатратам (например: что сделано, над чем работали)" rows={3} title="Комментарий к трудозатратам" style={{ width: '100%', resize: 'vertical', minHeight: 60, padding: '10px 12px', borderRadius: 8, border: '2px solid var(--border-color)', background: 'var(--darker-bg)', color: 'var(--text-primary)', fontSize: 14 }} />
                    </div>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: 120, overflowY: 'auto' }}>
                      {timeEntries.map(e => (
                        <li key={e.id} style={{ padding: '4px 0', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>{e.user_name || '—'} · {e.date?.slice(0,10)} · {e.hours} ч</span>
                          <button type="button" className="btn-danger btn-small" style={{ padding: '2px 6px', fontSize: 11 }} onClick={async () => { try { await axios.delete(`/api/projects/${selectedProject.id}/tasks/${editingTask.id}/time-entries/${e.id}`, { headers: headers() }); setTimeEntries(prev => prev.filter(x => x.id !== e.id)); fetchProjectData(selectedProject.id); } catch (err) { setError('Ошибка'); } }}>×</button>
                        </li>
                      ))}
                    </ul>
                    <div style={{ marginTop: 8 }}>
                      <button type="button" className={timerRunning === editingTask.id ? 'btn-danger btn-small' : 'btn-success btn-small'} onClick={async () => {
                        if (timerRunning === editingTask.id) {
                          try { await axios.post(`/api/projects/${selectedProject.id}/tasks/${editingTask.id}/time-entries/stop`, { started_at: timerStartedAt, comment: '' }, { headers: headers() }); setTimerRunning(null); setTimerStartedAt(null); const r = await axios.get(`/api/projects/${selectedProject.id}/tasks/${editingTask.id}/time-entries`, { headers: headers() }); setTimeEntries(r.data.time_entries || []); fetchProjectData(selectedProject.id); } catch (err) { setError('Ошибка'); }
                        } else { setTimerRunning(editingTask.id); setTimerStartedAt(new Date().toISOString()); }
                      }}>{timerRunning === editingTask.id ? '⏹ Стоп таймер' : '▶ Старт таймер'}</button>
                      {timerRunning === editingTask.id && <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-secondary)' }}>Таймер запущен</span>}
                    </div>
                  </>
                ) : (
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Сохраните задачу, чтобы добавить трудозатраты и таймер.</p>
                )}
              </div>
              <div className="form-group">
                <label>Зависимости (зависит от)</label>
                {editingTask ? (
                  <>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <select style={{ flex: 1 }} onChange={async (e) => { const v = e.target.value; if (!v) return; try { await axios.post(`/api/projects/${selectedProject.id}/tasks/${editingTask.id}/dependencies`, { depends_on_id: parseInt(v) }, { headers: headers() }); const r = await axios.get(`/api/projects/${selectedProject.id}/tasks/${editingTask.id}/dependencies`, { headers: headers() }); setTaskDependencies(r.data.dependencies || []); fetchProjectData(selectedProject.id); e.target.value = ''; } catch (err) { setError(err.response?.data?.detail || 'Ошибка'); } }}>
                        <option value="">+ Добавить зависимость</option>
                        {flattenTasks(tasks).filter(t => t.id !== editingTask.id && !taskDependencies.some(d => d.depends_on_id === t.id)).map(t => <option key={t.id} value={t.id}>{t.wbs_code} {t.name}</option>)}
                      </select>
                    </div>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: 80, overflowY: 'auto' }}>
                      {taskDependencies.map(d => (
                        <li key={d.id} style={{ padding: '4px 0', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>{d.depends_on_name || d.depends_on_id} ({d.type || 'FS'})</span>
                          <button type="button" className="btn-danger btn-small" style={{ padding: '2px 6px', fontSize: 11 }} onClick={async () => { try { await axios.delete(`/api/projects/${selectedProject.id}/tasks/${editingTask.id}/dependencies/${d.id}`, { headers: headers() }); setTaskDependencies(prev => prev.filter(x => x.id !== d.id)); fetchProjectData(selectedProject.id); } catch (err) { setError('Ошибка'); } }}>×</button>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Сохраните задачу, чтобы добавить зависимости.</p>
                )}
              </div>
              <div className="form-group">
                <label>Ресурсы (оборудование / материалы)</label>
                {editingTask ? (
                  <>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <select style={{ flex: 1, minWidth: 140 }} id="alloc-resource" defaultValue="" title="Выберите оборудование или материал для назначения на задачу">
                        <option value="">— Выберите ресурс</option>
                        {catalogEquipment.map(e => <option key={`eq-${e.id}`} value={`equipment:${e.id}`}>{e.name} (оборуд.)</option>)}
                        {catalogMaterials.map(m => <option key={`mat-${m.id}`} value={`material:${m.id}`}>{m.name} (мат.)</option>)}
                      </select>
                      <input type="number" min="1" defaultValue="1" placeholder="Кол-во" style={{ width: 70 }} id="alloc-qty" title="Количество единиц ресурса" />
                      <button type="button" className="btn-success btn-small" onClick={async () => { const sel = document.getElementById('alloc-resource'); const qtyInp = document.getElementById('alloc-qty'); const val = sel?.value; if (!val) return; const [type, idStr] = val.split(':'); const id = parseInt(idStr, 10); const qty = parseInt(qtyInp?.value || '1', 10) || 1; const payload = type === 'equipment' ? { resource_type: 'equipment', catalog_equipment_id: id, quantity: qty } : { resource_type: 'material', catalog_material_id: id, quantity: qty }; try { await axios.post(`/api/projects/${selectedProject.id}/tasks/${editingTask.id}/resource-allocations`, payload, { headers: headers() }); const r = await axios.get(`/api/projects/${selectedProject.id}/tasks/${editingTask.id}/resource-allocations`, { headers: headers() }); setResourceAllocations(r.data.allocations || []); fetchProjectData(selectedProject.id); sel.value = ''; qtyInp.value = '1'; } catch (err) { setError(err.response?.data?.detail || 'Ошибка'); } }}>+ Добавить</button>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                      Нет в списке?{' '}
                      <button type="button" className="btn-secondary btn-small" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => { setEditingEquipment(null); setEquipmentForm({ name: '', description: '', unit: 'шт' }); setShowEquipmentModal(true); }}>+ Добавить оборудование</button>
                      {' '}
                      <button type="button" className="btn-secondary btn-small" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => { setEditingMaterial(null); setMaterialForm({ name: '', description: '', unit: 'шт' }); setShowMaterialModal(true); }}>+ Добавить материал</button>
                    </div>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: 80, overflowY: 'auto' }}>
                      {resourceAllocations.map(a => (
                        <li key={a.id} style={{ padding: '4px 0', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>{a.name || '—'} × {a.quantity} {(a.unit || 'шт').trim()}{a.hours ? ` (${a.hours} ч)` : ''}</span>
                          <button type="button" className="btn-danger btn-small" style={{ padding: '2px 6px', fontSize: 11 }} onClick={async () => { try { await axios.delete(`/api/projects/${selectedProject.id}/tasks/${editingTask.id}/resource-allocations/${a.id}`, { headers: headers() }); setResourceAllocations(prev => prev.filter(x => x.id !== a.id)); fetchProjectData(selectedProject.id); } catch (err) { setError('Ошибка'); } }}>×</button>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Сохраните задачу, чтобы добавить оборудование и материалы.</p>
                )}
              </div>
              <div className="form-group">
                <label>Ответственный</label>
                <select value={taskForm.responsible_id || ''} onChange={e => setTaskForm({ ...taskForm, responsible_id: e.target.value ? parseInt(e.target.value) : null })} title="Исполнитель задачи">
                  <option value="">—</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}
                </select>
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-success">{editingTask ? 'Сохранить' : 'Создать'}</button>
                <button type="button" className="btn-secondary" onClick={() => { setShowTaskModal(false); setEditingTask(null); }}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteProjectConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteProjectConfirm(null)}>
          <div className="confirmation-dialog" onClick={e => e.stopPropagation()}>
            <h3>Удалить проект?</h3>
            <p>Проект «{deleteProjectConfirm.name}» и все данные будут удалены.</p>
            <div className="confirmation-actions">
              <button type="button" className="btn-danger btn-small" onClick={deleteProject}>Удалить</button>
              <button type="button" className="btn-secondary btn-small" onClick={() => setDeleteProjectConfirm(null)}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {deleteTaskConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteTaskConfirm(null)}>
          <div className="confirmation-dialog" onClick={e => e.stopPropagation()}>
            <h3>Удалить задачу?</h3>
            <p>Задача «{deleteTaskConfirm.name}» будет удалена.</p>
            <div className="confirmation-actions">
              <button type="button" className="btn-danger btn-small" onClick={deleteTask}>Удалить</button>
              <button type="button" className="btn-secondary btn-small" onClick={() => setDeleteTaskConfirm(null)}>Отмена</button>
            </div>
          </div>
        </div>
      )}
      {deleteConferenceConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConferenceConfirm(null)}>
          <div className="confirmation-dialog" onClick={e => e.stopPropagation()}>
            <h3>Удалить ВКС?</h3>
            <p>Конференция «{deleteConferenceConfirm.title}» будет удалена.</p>
            <div className="confirmation-actions">
              <button type="button" className="btn-danger btn-small" onClick={deleteConference}>Удалить</button>
              <button type="button" className="btn-secondary btn-small" onClick={() => setDeleteConferenceConfirm(null)}>Отмена</button>
            </div>
          </div>
        </div>
      )}
      {showConferenceModal && (
        <div className="modal-overlay" onClick={() => { setShowConferenceModal(false); setEditingConference(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editingConference ? 'Редактировать ВКС' : 'Создать ВКС'}</h2>
            <form onSubmit={editingConference ? updateConference : createConference}>
              <div className="form-group">
                <label>Название</label>
                <input type="text" value={conferenceForm.title} onChange={e => setConferenceForm({ ...conferenceForm, title: e.target.value })} required placeholder="Название конференции" title="Название ВКС для отображения в расписании" />
              </div>
              <div className="form-group">
                <label>Дата и время</label>
                <input type="datetime-local" value={conferenceForm.scheduled_at} onChange={e => setConferenceForm({ ...conferenceForm, scheduled_at: e.target.value })} required title="Дата и время начала конференции (нажмите на иконку календаря для выбора)" />
              </div>
              <div className="form-group">
                <label>Длительность (мин)</label>
                <input type="number" min={15} value={conferenceForm.duration_minutes} onChange={e => setConferenceForm({ ...conferenceForm, duration_minutes: parseInt(e.target.value, 10) || 60 })} title="Длительность конференции в минутах (не менее 15)" />
              </div>
              <div className="form-group">
                <label>Участники (уведомления в Telegram)</label>
                <select multiple value={(conferenceForm.participant_ids || []).map(String)} onChange={e => { const opts = e.target.selectedOptions; setConferenceForm({ ...conferenceForm, participant_ids: Array.from(opts).map(o => parseInt(o.value, 10)) }); }} style={{ minHeight: 100 }} title="Удерживайте Ctrl (Cmd) для выбора нескольких участников. Им придёт уведомление в Telegram">
                  {users.filter(u => u.id !== currentUser?.id).map(u => <option key={u.id} value={String(u.id)}>{u.full_name || u.username}</option>)}
                </select>
                <small style={{ display: 'block', marginTop: 4 }}>Удерживайте Ctrl (Cmd) для выбора нескольких</small>
              </div>
              <div className="confirmation-actions" style={{ marginTop: 16 }}>
                <button type="submit" className="btn-success btn-small">{editingConference ? 'Сохранить' : 'Создать'}</button>
                <button type="button" className="btn-secondary btn-small" onClick={() => { setShowConferenceModal(false); setEditingConference(null); }}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showClientModal && (
        <div className="modal-overlay" onClick={() => { setShowClientModal(false); setEditingClient(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editingClient ? 'Редактировать клиента' : 'Добавить клиента'}</h2>
            <form onSubmit={editingClient ? updateClient : createClient}>
              <div className="form-group">
                <label>Название *</label>
                <input value={clientForm.name} onChange={e => setClientForm({ ...clientForm, name: e.target.value })} required title="Название организации-клиента" />
              </div>
              <div className="form-group">
                <label>Контактное лицо</label>
                <input value={clientForm.contact_person} onChange={e => setClientForm({ ...clientForm, contact_person: e.target.value })} title="ФИО контактного лица" />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={clientForm.email} onChange={e => setClientForm({ ...clientForm, email: e.target.value })} title="Email клиента" />
              </div>
              <div className="form-group">
                <label>Телефон</label>
                <input value={clientForm.phone} onChange={e => setClientForm({ ...clientForm, phone: e.target.value })} title="Телефон клиента" />
              </div>
              <div className="form-group">
                <label>Адрес</label>
                <input value={clientForm.address} onChange={e => setClientForm({ ...clientForm, address: e.target.value })} title="Адрес клиента" />
              </div>
              <div className="form-group">
                <label>Заметки</label>
                <textarea value={clientForm.notes} onChange={e => setClientForm({ ...clientForm, notes: e.target.value })} title="Дополнительные заметки по клиенту" />
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-success">{editingClient ? 'Сохранить' : 'Добавить'}</button>
                <button type="button" className="btn-secondary" onClick={() => { setShowClientModal(false); setEditingClient(null); }}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEquipmentModal && (
        <div className="modal-overlay" onClick={() => { setShowEquipmentModal(false); setEditingEquipment(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editingEquipment ? 'Редактировать оборудование' : 'Добавить оборудование'}</h2>
            <form onSubmit={async (e) => { e.preventDefault(); try { if (editingEquipment) { await axios.put(`/api/catalog/equipment/${editingEquipment.id}`, equipmentForm, { headers: headers() }); setSuccess('Оборудование обновлено'); } else { await axios.post('/api/catalog/equipment', equipmentForm, { headers: headers() }); setSuccess('Оборудование добавлено'); } setShowEquipmentModal(false); setEditingEquipment(null); fetchCatalogAndDeals(); } catch (err) { setError(err.response?.data?.detail || 'Ошибка'); } }}>
              <div className="form-group"><label>Название *</label><input value={equipmentForm.name} onChange={e => setEquipmentForm({ ...equipmentForm, name: e.target.value })} required title="Название оборудования (для назначения на задачи)" /></div>
              <div className="form-group"><label>Описание</label><textarea value={equipmentForm.description || ''} onChange={e => setEquipmentForm({ ...equipmentForm, description: e.target.value })} rows={2} title="Описание оборудования" /></div>
              <div className="form-group"><label>Единица</label><input value={equipmentForm.unit} onChange={e => setEquipmentForm({ ...equipmentForm, unit: e.target.value })} placeholder="шт" title="Единица измерения (шт, комплект и т.д.)" /></div>
              <div className="modal-actions"><button type="submit" className="btn-success">{editingEquipment ? 'Сохранить' : 'Добавить'}</button><button type="button" className="btn-secondary" onClick={() => { setShowEquipmentModal(false); setEditingEquipment(null); }}>Отмена</button></div>
            </form>
          </div>
        </div>
      )}

      {showMaterialModal && (
        <div className="modal-overlay" onClick={() => { setShowMaterialModal(false); setEditingMaterial(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editingMaterial ? 'Редактировать материал' : 'Добавить материал'}</h2>
            <form onSubmit={async (e) => { e.preventDefault(); try { if (editingMaterial) { await axios.put(`/api/catalog/materials/${editingMaterial.id}`, materialForm, { headers: headers() }); setSuccess('Материал обновлён'); } else { await axios.post('/api/catalog/materials', materialForm, { headers: headers() }); setSuccess('Материал добавлен'); } setShowMaterialModal(false); setEditingMaterial(null); fetchCatalogAndDeals(); } catch (err) { setError(err.response?.data?.detail || 'Ошибка'); } }}>
              <div className="form-group"><label>Название *</label><input value={materialForm.name} onChange={e => setMaterialForm({ ...materialForm, name: e.target.value })} required title="Название материала (для назначения на задачи)" /></div>
              <div className="form-group"><label>Описание</label><textarea value={materialForm.description || ''} onChange={e => setMaterialForm({ ...materialForm, description: e.target.value })} rows={2} title="Описание материала" /></div>
              <div className="form-group"><label>Единица</label><input value={materialForm.unit} onChange={e => setMaterialForm({ ...materialForm, unit: e.target.value })} placeholder="шт" title="Единица измерения (шт, кг, м и т.д.)" /></div>
              <div className="modal-actions"><button type="submit" className="btn-success">{editingMaterial ? 'Сохранить' : 'Добавить'}</button><button type="button" className="btn-secondary" onClick={() => { setShowMaterialModal(false); setEditingMaterial(null); }}>Отмена</button></div>
            </form>
          </div>
        </div>
      )}

      {showDealModal && (
        <div className="modal-overlay" onClick={() => { setShowDealModal(false); setEditingDeal(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editingDeal ? 'Редактировать сделку' : 'Добавить сделку'}</h2>
            <form onSubmit={async (e) => { e.preventDefault(); try { const payload = { name: dealForm.name, client_id: dealForm.client_id, stage_id: dealForm.stage_id, amount: dealForm.amount || 0, project_id: dealForm.project_id || null, responsible_id: dealForm.responsible_id || null }; if (editingDeal) { await axios.put(`/api/crm/deals/${editingDeal.id}`, payload, { headers: headers() }); setSuccess('Сделка обновлена'); } else { await axios.post('/api/crm/deals', payload, { headers: headers() }); setSuccess('Сделка создана'); } setShowDealModal(false); setEditingDeal(null); fetchCatalogAndDeals(); } catch (err) { setError(err.response?.data?.detail || 'Ошибка'); } }}>
              <div className="form-group"><label>Название *</label><input value={dealForm.name} onChange={e => setDealForm({ ...dealForm, name: e.target.value })} required placeholder="Название сделки" title="Название сделки в CRM" /></div>
              <div className="form-group"><label>Клиент</label><select value={dealForm.client_id || ''} onChange={e => setDealForm({ ...dealForm, client_id: e.target.value ? parseInt(e.target.value) : null })} title="Клиент по сделке"><option value="">—</option>{clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
              <div className="form-group"><label>Этап</label><select value={dealForm.stage_id || ''} onChange={e => setDealForm({ ...dealForm, stage_id: e.target.value ? parseInt(e.target.value) : null })} title="Этап воронки продаж"><option value="">—</option>{dealStages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
              <div className="form-group"><label>Проект</label><select value={dealForm.project_id || ''} onChange={e => setDealForm({ ...dealForm, project_id: e.target.value ? parseInt(e.target.value) : null })} title="Связанный проект"><option value="">—</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
              <div className="form-group"><label>Сумма (руб)</label><input type="number" min={0} value={dealForm.amount || 0} onChange={e => setDealForm({ ...dealForm, amount: parseInt(e.target.value, 10) || 0 })} title="Сумма сделки в рублях" /></div>
              <div className="form-group"><label>Ответственный</label><select value={dealForm.responsible_id || ''} onChange={e => setDealForm({ ...dealForm, responsible_id: e.target.value ? parseInt(e.target.value) : null })} title="Менеджер по сделке"><option value="">—</option>{users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}</select></div>
              <div className="modal-actions"><button type="submit" className="btn-success">{editingDeal ? 'Сохранить' : 'Добавить'}</button><button type="button" className="btn-secondary" onClick={() => { setShowDealModal(false); setEditingDeal(null); }}>Отмена</button></div>
            </form>
          </div>
        </div>
      )}

      {showRiskModal && (
        <div className="modal-overlay" onClick={() => { setShowRiskModal(false); setEditingRisk(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editingRisk ? 'Редактировать риск' : 'Добавить риск'}</h2>
            <form onSubmit={editingRisk ? updateRisk : createRisk}>
              <div className="form-group">
                <label>Название *</label>
                <input value={riskForm.title} onChange={e => setRiskForm({ ...riskForm, title: e.target.value })} required title="Краткое название риска" />
              </div>
              <div className="form-group">
                <label>Описание</label>
                <textarea value={riskForm.description || ''} onChange={e => setRiskForm({ ...riskForm, description: e.target.value })} rows={2} title="Подробное описание риска" />
              </div>
              <div className="form-group">
                <label>Вероятность (%)</label>
                <input type="number" min="0" max="100" value={riskForm.probability} onChange={e => setRiskForm({ ...riskForm, probability: parseInt(e.target.value) || 50 })} title="Вероятность наступления риска (0–100%)" />
              </div>
              <div className="form-group">
                <label>Влияние (%)</label>
                <input type="number" min="0" max="100" value={riskForm.impact} onChange={e => setRiskForm({ ...riskForm, impact: parseInt(e.target.value) || 50 })} title="Влияние на проект при наступлении (0–100%)" />
              </div>
              <div className="form-group">
                <label>Меры по снижению</label>
                <textarea value={riskForm.mitigation || ''} onChange={e => setRiskForm({ ...riskForm, mitigation: e.target.value })} rows={2} title="Меры по снижению или предотвращению риска" />
              </div>
              <div className="form-group">
                <label>Статус</label>
                <select value={riskForm.status || 'open'} onChange={e => setRiskForm({ ...riskForm, status: e.target.value })} title="Текущий статус риска">
                  <option value="open">Открыт</option>
                  <option value="mitigated">Снижен</option>
                  <option value="closed">Закрыт</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-success">{editingRisk ? 'Сохранить' : 'Добавить'}</button>
                <button type="button" className="btn-secondary" onClick={() => { setShowRiskModal(false); setEditingRisk(null); }}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteRiskConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteRiskConfirm(null)}>
          <div className="confirmation-dialog" onClick={e => e.stopPropagation()}>
            <h3>Удалить риск?</h3>
            <p>Риск «{deleteRiskConfirm.title}» будет удалён.</p>
            <div className="confirmation-actions">
              <button type="button" className="btn-danger btn-small" onClick={deleteRisk}>Удалить</button>
              <button type="button" className="btn-secondary btn-small" onClick={() => setDeleteRiskConfirm(null)}>Отмена</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManagement;
